// Civic pixel beacon. This module is bundled by `scripts/build.mjs` into a
// single self-executing browser asset published to `bryn-pixel-<env>/`. The
// exported functions exist so the behaviour is unit-testable without a DOM; the
// repo targets the ES2022 lib (no DOM types), so the browser surface is modelled
// with the minimal structural interfaces below and injected at the call site.

// ---- Beacon delivery diagnostics (BRYN-1962) ----
//
// `window.__brynPixelDiagnostics` exposes the beacon's per-leg state for the
// current page so a customer (or support) can tell a working beacon from a
// blocked one without console access. It is a separate global from `window.bryn`
// deliberately: `window.bryn` is forwarded verbatim into the tenant's PostHog
// person via `setPersonProperties`, and diagnostics must never ride along.
//
// `publish` covers the OUT leg — was a beacon sent, and how?
//   - `sent-beacon`  `navigator.sendBeacon` accepted the payload
//   - `sent-fetch`   a keepalive fetch was issued (sendBeacon was unavailable
//     or its transport quota was full)
//   - `failed`       both transports rejected or threw (the browser blocked
//     the request, the endpoint was unreachable, etc.)
//   - `unavailable`  the page did not actually run the beacon (no `fetch`,
//     no `sendBeacon`, no `navigator` — server, import-only, or a test)
//
// `serve` covers the RETURN leg — what did the serve endpoint do?
//   - `lastOutcome`       one of `ok` (a payload parsed), `network-error`
//     (fetch rejected OR `ok: false`), or a numeric HTTP status
//   - `consecutiveFailures` how many bad responses in a row (resets on a
//     parsed payload; identical to the loop's failure counter)
//   - `loopState`         `polling` while a due poll is in flight, `paused`
//     while a poll is parked waiting on tab visibility, `stopped` once the
//     loop has ended for any reason
//   - `stopReason`        present only when `loopState === "stopped"` —
//     `suppressed` (terminal serve payload), `consecutive-failures`
//     (the loop's failure cap), `pending-ceiling` (never resolved, ran out
//     of attempts), `host-unload` (pagehide/beforeunload fired),
//     `idle-ceiling` (BRYN-1959: a resolved tab with no payload change in
//     30 minutes — distinct from the four documented reasons because it
//     reflects a healthy tab that simply did not need anything for a while),
//     `collection-only` (BRYN-1960: the serve response carried
//     `X-Bryn-Return-Leg: off`, or the snippet opted out so the loop never started)
export type PixelDiagnostics = {
  publish: { state: "sent-beacon" | "sent-fetch" | "failed" | "unavailable" };
  serve: {
    lastOutcome: "ok" | "network-error" | number;
    consecutiveFailures: number;
    loopState: "polling" | "paused" | "stopped";
    stopReason?:
      | "suppressed"
      | "consecutive-failures"
      | "pending-ceiling"
      | "host-unload"
      | "idle-ceiling"
      | "collection-only";
  };
};

export type PublishLeg = PixelDiagnostics["publish"]["state"];
export type ServeLoopState = PixelDiagnostics["serve"]["loopState"];
export type ServeStopReason = NonNullable<PixelDiagnostics["serve"]["stopReason"]>;

const DELIVERY_EVENT = "bryn:delivery";

export type PixelPayload = {
  url: string;
  referrer: string;
  ts: number;
  // The visit's FIRST touch, replayed on every beacon after the first (BRYN-1586). `url`/`referrer`
  // above always describe the CURRENT page, so without these the campaign UTMs die on the landing
  // page and the page a visitor happens to identify on is the one that gets attributed. Absent on
  // the visit's own first beacon (its `url` already is the first touch) and whenever session
  // storage is unavailable — the server treats both the same way.
  firstUrl?: string;
  firstReferrer?: string;
};

export type PixelConfig = {
  pixelRef: string;
  endpoint: string;
  /**
   * Collection-only switch (BRYN-1960): when `true` (the default), the beacon arms the
   * return leg and surfaces payloads; when `false`, the publish beacon still goes out but the
   * poll loop never starts. The "off" reading is mutually exclusive with the serve response's
   * `X-Bryn-Return-Leg: off` header — the header governs per-tenant server state, while this
   * controls the customer's own snippet (no return leg, no personalization). The serve response
   * carrying the header is the response-time mirror of this opt-in (see {@link returnLegOff}).
   */
  personalize: boolean;
};

// Config supplied via a first-party `window.__brynPixel` global rather than the
// host `<script>` data attributes. This is the tag-manager path: a GTM custom
// template can inject `pixel.js` but cannot set `data-*` attributes on the
// injected script, so it publishes the ref (and optional endpoint) on this
// global instead. Both fields fall back behind their `data-*` equivalents.
// `debug` enables `console.debug` lines for diagnostics transitions; it never
// turns on diagnostics themselves — those are always on (BRYN-1962). Kept here
// rather than added as another `data-*` attribute because the GTM path is the
// one without access to script attributes. `personalize` (BRYN-1960) is the
// collection-only switch at the snippet level — only a boolean `false` is honoured,
// so a missing field, an `undefined`, a string, or `true` are all "personalize".
export type PixelGlobalConfig = {
  ref?: string;
  endpoint?: string;
  debug?: boolean;
  personalize?: boolean;
};

// The return leg (BRYN-594, BRYN-933, BRYN-942). The serve payload the beacon retrieves
// and surfaces inline: `status` drives the poll loop; `entity` carries company-level
// attributes + active Signal labels; `individual` carries the resolved person's
// attributes + their signals, or is null when no person resolved. Each section may
// include a stable identity-stitch key — `entity.entityId` (the resolved company's id)
// and `individual.individualId` (the resolved person's id) — for identity stitching. A
// payload is written to the visitor's PostHog person by the beacon itself
// (`createPosthogSync`, BRYN-1601) — any authorised, non-`suppressed` one, `pending` included,
// which is what carries a wrongly-resolved visitor's `linkId` to the correction bridge; a
// tenant who wants their own identity semantics reads
// the same keys off `window.bryn`/`bryn:personalize` and calls `posthog.identify()` /
// `posthog.group()` on top. Display attributes arrive only with `status: "resolved"`, but
// `entity.entityId` / `entity.domain` travel as soon as the visitor's Entity exists — on a
// `"pending"` payload too (BRYN-1393) — so an unscored visit is still attributable to an
// account; likewise an `individual` section holding only `individualId` (an anonymous
// visitor's placeholder person, BRYN-1656). Attributes are allowlist-filtered server-side.
//
// NOTE: intentionally duplicated from `apps/pipeline-ingest/src/serve/payload.ts`
// (`PixelServePayload`). The pixel is a standalone, import-free browser bundle
// (scripts/build.mjs transpiles this single module), so it can't share the server
// type. Keep the two shapes — and the OpenAPI schema in pixelServe.ts — in sync.
export type ServeStatus = "pending" | "resolved" | "suppressed";

// Attributes live at the section's top level (BRYN-933 flatten, decision 2026-07-01);
// `signals` is the one reserved key, every other key is a display attribute string.
export type ServeSection = {
  signals: string[];
  [attribute: string]: string | string[];
};

export type ServePayload = {
  status: ServeStatus;
  entity: ServeSection;
  individual: ServeSection | null;
  /**
   * Opaque correction handle (BRYN-1382). Not a display attribute and deliberately outside both DTO
   * sections — it is plumbing that must survive the round trip to the host page, because the
   * identity-stitch recipe stores this whole payload as an analytics person property and the
   * correction depends on reading it back. Dropping it here silently disables that mechanism end to
   * end, with nothing on either side reporting an error. Present only on a resolved payload.
   */
  linkId?: string;
};

type ScriptLike = {
  getAttribute(name: string): string | null;
};

type DocumentLike = {
  referrer: string;
  currentScript?: ScriptLike | null;
  // Fallback locator for the pixel's own <script> when `currentScript` is null (see
  // `findScript`). Optional so a host that wires neither is a silent no-op.
  querySelector?: (selector: string) => ScriptLike | null;
  // BRYN-1972: needed by `findScript` when two different pixel refs share a page (or the
  // loader injects a bundle tag that copies the loader's `data-bryn-pixel-ref`, so the page
  // carries two tags with the SAME ref). `querySelector` always returns the first match; we
  // have to walk every tag and pick the one whose ref is unclaimed. Optional, like
  // `querySelector`: a host that wires neither is a silent no-op, and the `findScript`
  // implementation falls back to today's `querySelector` path when it is absent or throws.
  querySelectorAll?: (selector: string) => ArrayLike<ScriptLike>;
  // Page Visibility API (BRYN-1959): `visibilityState === "hidden"` parks pending serve
  // polls so a backgrounded tab stops fetching; a `visibilitychange` listener fires them on
  // return. Modelled structurally because the repo targets the ES2022 lib with no DOM types.
  visibilityState?: string;
  addEventListener?: (type: string, handler: () => void) => void;
};

type LocationLike = {
  href: string;
};

type NavigatorLike = {
  sendBeacon?(url: string, data?: unknown): boolean;
};

type FetchInit = {
  method: string;
  headers: Record<string, string>;
  body: string;
  keepalive: boolean;
};

type FetchLike = (url: string, init: FetchInit) => unknown;
type BlobFactory = (body: string, type: string) => unknown;
type BlobCtor = new (parts: string[], options?: { type?: string }) => unknown;

// Return-leg (retrieve) browser surface, modelled structurally and injected at the
// call site like the publish surface above. All optional on PixelEnv: a host that
// wires none of them simply never retrieves (the publish leg is unaffected).
//
// The DOM personalization surface (`data-bryn-*`, below) reads an element's own marker
// attribute (`getAttribute`) and either sets its text (`textContent`) or toggles the
// standard `hidden` attribute (`set`/`removeAttribute`) — no untrusted value is ever
// interpolated into a selector or written as HTML.
type ElementLike = {
  textContent: string | null;
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
};
/** The response headers the beacon reads (only `X-Bryn-Posthog-Sync`; see {@link posthogSyncAllowed}). */
type HeadersLike = { get(name: string): string | null };
type ServeResponseLike = {
  ok: boolean;
  /**
   * HTTP status code. Optional in the structural model so a host that wraps `fetch`
   * and doesn't surface `status` still satisfies the type — the diagnostics publisher
   * defaults to "network-error" when `status` is absent. The pixel itself never sets
   * `status` to `undefined` on the diagnostic surface; `0` is the synthetic value used
   * inside `attempt` when the host response object lacks a status field.
   */
  status?: number;
  json(): Promise<unknown>;
  headers?: HeadersLike;
};
type ServeFetchLike = (url: string) => Promise<ServeResponseLike>;
type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};
type TimerLike = (handler: () => void, ms: number) => unknown;
type DispatchLike = (type: string, detail: unknown) => void;
type PublishLike = (payload: ServePayload) => void;
type QueryAllLike = (selector: string) => ArrayLike<ElementLike>;

export type PixelEnv = {
  document: DocumentLike;
  location: LocationLike;
  navigator?: NavigatorLike;
  fetch?: FetchLike;
  blob?: BlobFactory;
  now: () => number;
  // Return leg (optional):
  serveFetch?: ServeFetchLike;
  setTimeout?: TimerLike;
  storage?: StorageLike;
  dispatch?: DispatchLike;
  publish?: PublishLike;
  queryAll?: QueryAllLike;
  /**
   * Reader for the host page's posthog-js instance (BRYN-1601). A GETTER, not the instance:
   * `window.posthog` is assigned by PostHog's own loader, which routinely runs AFTER the pixel
   * (async snippet, tag manager, a deferred bundle), so a value captured at init would be pinned
   * to `undefined` for the whole page. Read on every poll instead, which is the "check on a
   * timer" the serve loop already gives us for free.
   */
  posthog?: () => unknown;
  /**
   * Page-visibility surface, structurally modelled (the repo targets the ES2022 lib with no DOM
   * types). The serve poll loop pauses while the tab is hidden: a parked poll does not fetch,
   * does not count as an attempt, and does not reset the failure counter; on becoming visible
   * again, the parked poll runs immediately. `hidden()` reports the current state; `onChange`
   * is called back on a `visibilitychange` event so the loop can re-arm.
   */
  visibility?: { hidden(): boolean; onChange(handler: () => void): void };
  /**
   * Page-unload surface (BRYN-1962). Wired on autostart from `window.addEventListener`
   * for `pagehide` (preferred — fires on bfcache, mobile background, real unload) with
   * `beforeunload` as a fallback on hosts that lack it. The handler receives `pagehide`'s
   * `persisted` flag: a real unload ends the serve loop with stop reason `host-unload` so a
   * customer support console can distinguish "tab closed" from the other stop reasons, and a
   * page entering the back/forward cache (`persisted: true`) leaves the loop alive to resume
   * on restore. A host that wires neither runs the loop until
   * the tab is fully torn down — fine, the loop's listeners die with the page.
   */
  pageUnload?: { onHide(handler: (persisted: boolean) => void): void };
  /**
   * Beacon delivery diagnostics publisher (BRYN-1962). Wired on autostart from the
   * ambient `CustomEvent` / `dispatchEvent` / `window` surfaces; tests inject a stub. The
   * publisher never throws (a non-writable `__brynPixelDiagnostics` or a missing `dispatchEvent`
   * is swallowed), so a hostile page cannot break the beacon's poll loop by defining the
   * global as a getter.
   */
  diagnostics?: DiagnosticsPublisher;
  /**
   * Lazy diagnostics inputs (BRYN-1962). `autostart` wires these when it does not have
   * a stub publisher from a test harness; `init` constructs the actual publisher AFTER
   * the single-instance claim succeeds, so a second install of the same bundle never
   * overwrites the live diagnostics snapshot before its `init` returns false. Either
   * `diagnostics` (a ready publisher) or `diagnosticsInputs` (a factory pair) may be
   * set; `diagnostics` wins when both are present.
   */
  diagnosticsInputs?: { global: PixelGlobal; debug: boolean };
  /**
   * Init-time single-instance guard (BRYN-1958). Called with the resolved pixel ref AFTER
   * `resolveConfig` succeeds; if it returns false, {@link init} returns false without firing
   * the publish beacon or arming the return leg. Defaults to "no guard" — a host that wires
   * nothing gets the pre-existing behaviour. The intended `autostart` implementation claims a
   * ref in a global `Symbol.for` registry so two installs of the bundle on one page (the
   * site + GTM double-install path) end up with one running instance per ref.
   */
  claim?: (pixelRef: string) => boolean;
  /**
   * Read-only counterpart to {@link claim} (BRYN-1972). `findScript` consults it on the
   * `querySelectorAll` fallback to pick the first tag whose ref has not already been
   * claimed — two different refs on one page must both send, but two tags with the same
   * ref (the loader + injected bundle pair, BRYN-1961) must collapse to one. `findScript`
   * does NOT mutate the registry; the actual claim still happens through `claim`, after
   * `resolveConfig` reads the ref off the chosen tag. A host that wires neither `claim`
   * nor `isClaimed` falls back to today's `querySelector` behaviour: first tag wins.
   */
  isClaimed?: (pixelRef: string) => boolean;
};

/**
 * The diagnostics surface: writes the snapshot to `window.__brynPixelDiagnostics`
 * and fires a `bryn:delivery` CustomEvent with the same object as `detail`.
 *
 * Two writes (`update`/`stop`) and the structure mirrors `applyServePayload`'s
 * `publish` sink so a host page that defines a non-writable or accessor
 * property for the diagnostics global can't break the poll loop or the next
 * transition. The snapshot itself is mutated in place: `update` mutates the
 * SAME object the global holds, so a reader that captured a reference sees
 * the latest values without re-reading the global.
 */
export type DiagnosticsPublisher = {
  publish(partial: Partial<PixelDiagnostics["publish"]>): void;
  serve(partial: Partial<PixelDiagnostics["serve"]>): void;
  stop(reason: ServeStopReason): void;
};

type PixelGlobal = {
  document?: DocumentLike;
  location?: LocationLike;
  navigator?: NavigatorLike;
  fetch?: FetchLike;
  Blob?: BlobCtor;
  setTimeout?: (handler: () => void, ms: number) => unknown;
  sessionStorage?: StorageLike;
  dispatchEvent?: (event: unknown) => boolean;
  CustomEvent?: new (type: string, init?: { detail?: unknown }) => unknown;
  // Window-level addEventListener — used by the page-unload handler (BRYN-1962).
  // Optional so SSR / test environments without `window` simply do not wire it.
  addEventListener?: (type: string, handler: (event: unknown) => void) => void;
  // The most-recent applied serve payload, published for synchronous reads (decision 4a).
  bryn?: ServePayload;
  // First-party config global set by the tag-manager template (untrusted shape).
  __brynPixel?: unknown;
  // The host page's posthog-js instance, if it runs one (untrusted shape; see `readPosthog`).
  posthog?: unknown;
  // Beacon delivery diagnostics (BRYN-1962). Written by the publisher on every state
  // transition; the same object is dispatched on `bryn:delivery`. Kept separate from
  // `bryn` deliberately — the latter is forwarded into the tenant's PostHog person,
  // and diagnostics must never ride along.
  __brynPixelDiagnostics?: PixelDiagnostics;
};

// Beacon path under the Civic origin. The pixel posts to `<base>/<pixelRef>`.
// The script is Civic-hosted, so when no explicit `data-endpoint` is given the
// base is derived from the host `<script src>` origin — a relative "/pixel"
// would resolve against the embedding customer page and silently lose every
// beacon, since the script runs in the customer's browser, not on Civic.
const PIXEL_PATH = "/pixel";
const ORIGIN_RE = /^(https?:\/\/[^/]+)/i;

const deriveDefaultBase = (script: ScriptLike): string | null => {
  const origin = script.getAttribute("src")?.match(ORIGIN_RE)?.[1];
  return origin ? `${origin}${PIXEL_PATH}` : null;
};

/**
 * Resolve the pixel's own `<script>` element. `document.currentScript` is the fast path
 * — it is set during a script's top-level run for a parser-inserted tag and, in
 * Blink/WebKit, for an async dynamically-injected one. Firefox reports null for an async
 * injected script, which is exactly the Google Tag Manager Custom HTML path: GTM strips
 * custom attributes off a pasted `<script>`, so the workaround appends the pixel tag in
 * JS instead (`data-bryn-pixel-ref` set via `setAttribute` survives). When `currentScript`
 * is null, fall back to finding that injected tag in the DOM — first by the ref attribute
 * it carries, then by the shared pixel `src` (for the tag-manager-template path where the
 * ref lives only on `window.__brynPixel`). Returns null when neither resolves (SSR or a
 * test import), which keeps {@link init} a silent no-op.
 *
 * Two browsers / install paths make a single `querySelector` insufficient (BRYN-1972):
 *
 *   - two different pixel refs on one page. `querySelector` returns the first tag, so
 *     the second ref never resolves and never sends;
 *   - the loader (BRYN-1961) injects a content-hashed bundle and copies `data-bryn-pixel-ref`
 *     onto the injected tag — the page now carries TWO tags with the same ref, and only
 *     one of them should claim the pixel.
 *
 * The fix walks every tag with the selector via `querySelectorAll`, picks the first whose
 * ref is not yet in the BRYN-1958 claim registry, and falls back to today's
 * `querySelector` paths when `querySelectorAll` is absent or throws. The `isClaimed`
 * predicate is passed in through `PixelEnv` — `findScript` never reads the registry
 * itself, so a host can wire a different policy without losing the helper. The fallback
 * path preserves today's behaviour EXACTLY: ref attribute first, then src prefix.
 *
 * The selector strings are constants owned by this module, so a real `querySelector`
 * only throws on a host that has monkey-patched the method. The lookups are wrapped so a
 * single bad call never escapes onto the page (the beacon runs on the customer page, and
 * one page-side throw here is a regression that takes init down with it).
 */
export const findScript = (env: PixelEnv): ScriptLike | null => {
  const doc = env.document;
  if (doc.currentScript) return doc.currentScript;

  // The `querySelectorAll` path covers the multi-ref page and the loader-injected bundle.
  // We only fall through to the single-`querySelector` paths when this is missing or
  // throws, so the behaviour on a host that wires nothing matches today's exactly.
  //
  // `walkerRan` is the signal the legacy `querySelector` fallback uses to decide
  // whether to skip its own ref-attribute selector: if the walker ran successfully,
  // every ref-bearing tag has already had its shot, and re-finding them here would
  // only re-resolve a claimed tag. If the walker never ran (absent) or threw (hostile
  // override), the legacy path runs unchanged — ref attribute first, then src.
  let walkerRan = false;
  const queryAll = doc.querySelectorAll;
  if (queryAll) {
    try {
      walkerRan = true;
      const tags = queryAll.call(doc, "script[data-bryn-pixel-ref]");
      const isClaimed = env.isClaimed;
      for (let i = 0; i < tags.length; i += 1) {
        const tag = tags[i];
        const ref = tag.getAttribute("data-bryn-pixel-ref");
        // No `isClaimed` wired ⇒ first tag with a non-empty ref wins (legacy behaviour).
        // `isClaimed` wired ⇒ skip tags whose ref has already been claimed.
        if (ref && (!isClaimed || !isClaimed(ref))) return tag;
      }
      // All matching tags were claimed (or every match carried no ref): fall through to
      // the src-based selector for the tag-manager-template path where the ref lives only
      // on `window.__brynPixel`. Returning null here would silently lose a page that has
      // exactly one ref-bearing tag, claimed by A, AND a tag-manager tag for B — the
      // latter still has to send.
    } catch {
      // Host-overridden `querySelectorAll` threw — fall through to today's path so the
      // first tag wins, matching pre-BRYN-1972 behaviour. Better to send twice (the
      // BRYN-1958 guard will reject the duplicate) than to take init down.
      walkerRan = false;
    }
  }

  const query = doc.querySelector;
  if (!query) return null;
  // BRYN-1972 (Codex P2): when the `querySelectorAll` walk ran and found every ref-bearing
  // tag already claimed, the legacy `querySelector("script[data-bryn-pixel-ref]")` would
  // STILL return the first claimed tag — ref A — and the script resolver would pick it
  // up again, attribute-overriding whatever `window.__brynPixel` names for ref B. The
  // claim guard then blocks ref B. Skip the ref-attribute selector when the walker ran:
  // it already gave every ref-bearing tag a fair shot, so a leftover match would only
  // re-find a claimed tag. Go straight to the src-only selector, which catches the
  // tag-manager-template tag (where the ref lives on `window.__brynPixel`, not on the
  // tag).
  try {
    return walkerRan
      ? // Match the prefix shared by the loader (`pixel.js`) and the content-
        // hashed bundle (`pixel.<hash>.js`). BRYN-1961: the loader is the
        // stable URL customers keep, but inside the bundle `currentScript` is
        // the INJECTED `<script>` and its src is the hashed name — the prefix
        // catches both. Keep this selector in sync with `loader.ts`'s
        // `findOwnScript`.
        //
        // The `:not([data-bryn-pixel-ref])` excludes ref-bearing tags from
        // this fallback (Codex P2, third pass): they also match the prefix
        // because every ref-bearing Civic script carries `src="/pixel/pixel.*"`,
        // and `querySelector` returns the FIRST match, which would be a ref-
        // bearing tag the walker already considered. Its `data-bryn-pixel-ref`
        // would then override whatever `window.__brynPixel` names for ref B,
        // and the claim guard would block B. Excluding them here keeps the
        // tag-manager-template tag (the only src-only Civic tag in the page)
        // resolvable, with no behaviour change for non-ref-bearing tags.
        (query.call(doc, `script[src*="${PIXEL_PATH}/pixel."]:not([data-bryn-pixel-ref])`) ?? null)
      : (
          query.call(doc, "script[data-bryn-pixel-ref]") ??
          query.call(doc, `script[src*="${PIXEL_PATH}/pixel."]`) ??
          null
        );
  } catch {
    // Host-overridden querySelector threw — fall through to a silent no-op rather than
    // take init down on the customer's page.
    return null;
  }
};

/** First-party session key holding the visit's first touch, so later pages can replay it. */
const FIRST_TOUCH_KEY = "bryn:first-touch";

/** The stored first touch of the current visit. */
type FirstTouch = { firstUrl?: string; firstReferrer?: string };

/**
 * Read the visit's first touch from session storage, recording the current page as it on the
 * visit's first beacon. Returns the STORED touch, so the first beacon returns `{}` — its own `url`
 * already is the first touch and repeating it would only pad the payload.
 *
 * This is the pixel's counterpart to PostHog's `$initial_*` person properties, and the server reads
 * it at the same precedence. `sessionStorage`, not `localStorage`, is deliberate: attribution
 * should follow one visit rather than persist indefinitely on the visitor's device, which is also
 * why there is no expiry to get wrong. The known limit is that it is per-tab — a visitor who opens
 * the landing page and then a second tab loses the first touch in that tab, which is strictly
 * better than today (where it is lost on the second *page*), and fixing it would mean
 * `localStorage` plus an explicit expiry: a different privacy question, not a bigger patch.
 *
 * Every storage access is wrapped: in a private window, with site data blocked, or over quota,
 * both `getItem` and `setItem` throw, and a beacon that reports the current page is always better
 * than a script that breaks the customer's page.
 */
export const readFirstTouch = (
  storage: StorageLike | undefined,
  current: { url: string; referrer: string },
): FirstTouch => {
  if (!storage) return {};
  try {
    const stored = storage.getItem(FIRST_TOUCH_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as unknown;
      // Untrusted: another script on the customer's page can write this key. Only strings are
      // carried through, and the server sanitizes them like any other URL it is handed.
      const record =
        typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : {};
      const firstUrl = typeof record.url === "string" ? record.url : undefined;
      const firstReferrer = typeof record.referrer === "string" ? record.referrer : undefined;
      // A stored blob with no usable URL is treated as absent and re-recorded below.
      if (firstUrl !== undefined) {
        return { firstUrl, ...(firstReferrer === undefined ? {} : { firstReferrer }) };
      }
    }
    storage.setItem(
      FIRST_TOUCH_KEY,
      JSON.stringify({ url: current.url, referrer: current.referrer }),
    );
  } catch {
    // Storage unavailable, blocked, or full — non-fatal; this beacon reports the current page only.
  }
  return {};
};

/**
 * Builds the beacon payload from the current page URL, referrer, and timestamp, plus the visit's
 * first touch replayed from session storage (absent on the visit's first beacon).
 */
export const buildPayload = (
  location: LocationLike,
  document: DocumentLike,
  now: number,
  storage?: StorageLike,
): PixelPayload => {
  const current = { url: location.href, referrer: document.referrer };
  return { ...current, ts: now, ...readFirstTouch(storage, current) };
};

/**
 * Resolves the pixel ref and ingest endpoint. The host `<script>` data
 * attributes win; a `window.__brynPixel` `fallback` (the tag-manager path) fills
 * each field when its attribute is absent. So: ref is `data-bryn-pixel-ref` else
 * `fallback.ref`; the base is `data-endpoint` else `fallback.endpoint` else the
 * script's own origin (`deriveDefaultBase`). Returns null — a silent no-op
 * rather than a thrown error in the page — when there is no script, no pixel
 * ref, or no absolute base (so a beacon is never posted to the embedding page's
 * origin).
 *
 * `personalize` (BRYN-1960) reads the collection-only switch from the host script's
 * `data-bryn-personalize` attribute (a truthy string ⇒ off) else from `fallback.personalize`
 * (narrowed to boolean `false` only). Any other value — absent, an empty string, the string
 * `"true"`, `null`, an object — keeps today's behaviour. Default is `true` (personalize).
 */
export const resolveConfig = (
  script: ScriptLike | null | undefined,
  fallback?: PixelGlobalConfig | null,
): PixelConfig | null => {
  if (!script) return null;
  const pixelRef = script.getAttribute("data-bryn-pixel-ref") ?? fallback?.ref;
  if (!pixelRef) return null;
  const base =
    script.getAttribute("data-endpoint") ?? fallback?.endpoint ?? deriveDefaultBase(script);
  if (!base) return null;
  const endpoint = `${stripTrailingSlashes(base)}/${encodeURIComponent(pixelRef)}`;
  // Collection-only switch: prefer the data attribute over the global (the global is the
  // fallback for tag-manager paths). Only a literal `false` on the global counts, so a string,
  // an object, or `undefined` is treated as "personalize". The attribute is checked for the
  // value `"off"` (case-insensitive); anything else, including absent, means personalize.
  const attrValue = script.getAttribute("data-bryn-personalize");
  const attrOff = typeof attrValue === "string" && attrValue.trim().toLowerCase() === "off";
  const globalOff = fallback?.personalize === false;
  const personalize = !(attrOff || globalOff);
  return { pixelRef, endpoint, personalize };
};

// A scan instead of `/\/+$/`, whose backtracking is polynomial on adversarial input.
const stripTrailingSlashes = (value: string): string => {
  let end = value.length;
  while (end > 0 && value[end - 1] === "/") end -= 1;
  return value.slice(0, end);
};

/**
 * Narrow `unknown` to a thenable with a usable `.catch`. A bare
 * `PromiseLike` type guards the resolve side but not the rejection handler — the fetch
 * fallback only calls `.catch`, so the guard is exactly what is needed. Host wrappers and
 * polyfills that return objects with `.catch(fn)` are caught; a plain thenable that only
 * implements `.then` is intentionally rejected (it has no rejection handler to consume).
 *
 * The guard returns a predicate (`value is ThenableWithCatch`) so callers do not need
 * `as`/`any` on the consumption site.
 */
type ThenableWithCatch = { catch(onrejected: (reason: unknown) => unknown): unknown };
const isThenable = (value: unknown): value is ThenableWithCatch => {
  if (value === null || typeof value !== "object") return false;
  const catchFn = (value as { catch?: unknown }).catch;
  return typeof catchFn === "function";
};

/**
 * Prefers navigator.sendBeacon (survives page unload); falls back to a
 * keepalive fetch. Returns the publish leg for the diagnostics publisher (BRYN-1962):
 * - `sent-beacon` `sendBeacon` accepted the payload
 * - `sent-fetch`  the fetch fallback was used (sendBeacon was unavailable or its
 *   transport quota was full)
 * - `failed`      both transports were available and the sendBeacon call rejected,
 *   while fetch threw synchronously — best read as "the browser blocked the request"
 * - `unavailable` neither transport was wired (server, test import, or a publisher
 *   that has no `navigator` and no `fetch`)
 *
 * The fetch result is fire-and-forget, but a rejected promise would still
 * surface as `unhandledRejection` on the host page (a network failure, an
 * ad blocker, a keepalive-quota error). The returned thenable is detached
 * with `.catch(() => {})` so the rejection is consumed inside the beacon,
 * not by the host's event loop.
 */
export const send = (env: PixelEnv, endpoint: string, payload: PixelPayload): PublishLeg => {
  const body = JSON.stringify(payload);
  const beacon = env.navigator?.sendBeacon;
  const fetchFn = env.fetch;
  if (beacon) {
    try {
      const data = env.blob ? env.blob(body, "application/json") : body;
      if (beacon.call(env.navigator, endpoint, data)) return "sent-beacon";
    } catch {
      // A throwing sendBeacon (or its Blob argument) falls through to the fetch path, so the
      // beacon is still attempted via the keepalive transport.
    }
  }
  if (fetchFn) {
    try {
      const result = fetchFn(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      });
      // Network failures and extension blocks reject asynchronously, after `send` has returned
      // `sent-fetch`, so the rejection is where the publish leg becomes `failed`.
      if (isThenable(result)) result.catch(() => env.diagnostics?.publish({ state: "failed" }));
      return "sent-fetch";
    } catch {
      return "failed";
    }
  }
  // sendBeacon was attempted but returned false AND no fetch fallback was wired:
  // record this as `failed` so a customer reading the diagnostics sees the send
  // was attempted but did not go through. (Previously `false` returned; the new
  // four-state leg is what the diagnostics publisher needs.)
  return beacon ? "failed" : "unavailable";
};

// ---- Return leg (retrieve channel, BRYN-594) ----

/** The serve endpoint for a config: the publish endpoint plus `/serve`. */
export const retrieveUrl = (config: PixelConfig): string => `${config.endpoint}/serve`;

// Poll-with-backoff (decision 2026-06-23: same-session delivery; BRYN-1008: keep
// polling after resolution; BRYN-1959: bound the loop). Polling starts shortly after the first
// publish and backs off exponentially until it settles into a steady cadence (RETRIEVE_STEADY_MS).
// Until the first `resolved` response the loop is bounded by RETRIEVE_MAX_PENDING_ATTEMPTS, so
// a visitor that never resolves stops instead of polling for the whole tab lifetime: a visitor
// with no resolvable identity, a tenant with the return leg disabled (serves `pending` by design),
// or one still awaiting enrichment past the window — including one whose pending payload already
// carries the account stitch key (BRYN-1393), which is deliberately not treated as a resolution.
// The server only returns `resolved` once there is displayable data (BRYN-1008), so this window
// must cover the resolve+enrich latency; it is a deliberate trade-off (higher = catch slower
// first resolutions same-session, lower = less futile polling from the majority of visitors who
// never resolve). Once a `resolved` payload arrives the cap is lifted and polling continues
// indefinitely — bounded only by RETRIEVE_MAX_CONSECUTIVE_FAILURES (five bad responses in a row,
// rejections OR `ok: false`, ends the loop) and, while the loop is healthy, by
// RETRIEVE_RESOLVED_IDLE_MS (a quiet resolved-phase tab — 30 minutes with no payload change —
// ends the loop, re-arming on the next visibilitychange). A pause while the tab is hidden parks
// a pending poll rather than fetching, so a visitor who backgrounds the page for an hour does not
// pay an hour of serve fetches. It also stops on a terminal `suppressed` status or page unload.
const RETRIEVE_START_MS = 1000;
const RETRIEVE_BASE_MS = 1000;
const RETRIEVE_STEADY_MS = 20000;
// Cap on consecutive pre-resolution polls (~2.5 min at the steady cadence); lifted once a
// `resolved` payload is seen. Sized to cover resolve+enrich latency without unbounded polling
// from never-resolving visitors — tune with serve load / enrichment timing.
const RETRIEVE_MAX_PENDING_ATTEMPTS = 12;
// Cap on consecutive serve failures (rejections OR `ok: false`). Five bad responses in a row
// end the loop: 429s from the ingest rate limiter, transient serve outages, and a serve URL
// that's stopped responding all look the same here, and none get better with more requests.
// Resets on any parsed payload — a 503 followed by a 200 is fine; a 503 followed by a 503
// followed by a 200 with the parse failing is also fine. Five in a row with no reset is not.
const RETRIEVE_MAX_CONSECUTIVE_FAILURES = 5;
// Resolved-phase idle ceiling. A resolved visitor whose data hasn't changed in 30 minutes is not
// going to change in the next minute either, so the loop ends and stands the PostHog sink down.
// A visibilitychange while idle re-arms exactly once — one poll, then the window restarts —
// so a visitor who returns to a backgrounded tab picks up whatever changed while they were away
// without the loop having to wait for a fresh setTimeout tick to find out.
const RETRIEVE_RESOLVED_IDLE_MS = 30 * 60_000;

/** Exponential backoff for poll attempt `n` (0-based), capped at `maxMs`. */
export const backoffDelay = (attempt: number, baseMs: number, maxMs: number): number =>
  Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt));

/** Plain-object guard (local, since the pixel bundle imports nothing). */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Narrow the untrusted `window.__brynPixel` global to a {@link PixelGlobalConfig},
 * keeping only string `ref`/`endpoint` and a boolean `debug` flag (BRYN-1962).
 * Returns undefined when it isn't a usable object, so a bad value is simply
 * ignored rather than breaking resolution.
 *
 * `personalize` (BRYN-1960) is narrowed to a literal boolean: only `false` (the opt-out)
 * carries through, since the absence of a flag is the default ("on"). `null`, an `object`, a
 * string, or `true` would all be a third state we have no answer for, so they fall back to
 * "personalize" rather than disabling the return leg — making `window.__brynPixel = {}`
 * (a typo'd empty object) safe.
 */
const readGlobalConfig = (value: unknown): PixelGlobalConfig | undefined => {
  if (!isRecord(value)) return undefined;
  const config: PixelGlobalConfig = {};
  if (typeof value.ref === "string") config.ref = value.ref;
  if (typeof value.endpoint === "string") config.endpoint = value.endpoint;
  if (typeof value.debug === "boolean") config.debug = value.debug;
  if (value.personalize === false) config.personalize = false;
  return config;
};

/** A conservative guard for one bryn-path segment: a plain identifier, never a prototype key. */
const isSafePathSegment = (segment: string): boolean =>
  /^[\w-]+$/.test(segment) && !FORBIDDEN_KEYS.has(segment);

// `suppressed` is the sole terminal status (BRYN-1008): the poll stops and caches on it.
// `resolved` is intentionally NOT terminal, so the pixel keeps polling for fresher
// entity/individual data; `pending` is the cold, keep-polling state.
const isTerminalStatus = (status: ServeStatus): boolean => status === "suppressed";

/**
 * Keys never copied from the (untrusted) serve JSON onto a plain object — a
 * prototype-pollution guard. The serve response is same-origin from Bryn, but the
 * beacon treats it defensively; these keys are never legitimate DTO attribute names.
 */
const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype"]);

/** Narrow an unknown JSON value to a {@link ServeSection}, keeping only string attributes/signals. */
const parseSection = (raw: unknown): ServeSection => {
  const attributes: Record<string, string> = {};
  if (isRecord(raw)) {
    for (const key of Object.keys(raw)) {
      // `signals` is the reserved list key, not an attribute; FORBIDDEN_KEYS guards prototype pollution.
      if (key === "signals" || FORBIDDEN_KEYS.has(key)) continue;
      const value = raw[key];
      if (typeof value === "string") attributes[key] = value;
    }
  }
  const rawSignals = isRecord(raw) ? raw.signals : undefined;
  const signals = Array.isArray(rawSignals)
    ? rawSignals.filter((s): s is string => typeof s === "string")
    : [];
  return { ...attributes, signals };
};

/** Narrow an unknown JSON value to a {@link ServePayload}, or null when it isn't one. */
export const parseServePayload = (raw: unknown): ServePayload | null => {
  if (!isRecord(raw)) return null;
  if (raw.status !== "pending" && raw.status !== "resolved" && raw.status !== "suppressed")
    return null;
  const individual = isRecord(raw.individual) ? parseSection(raw.individual) : null;
  // `linkId` rides through unparsed into the published payload: the host page never renders it, but
  // the identity stitch forwards this object verbatim and the correction bridge reads it back.
  const linkId =
    typeof raw.linkId === "string" && raw.linkId.length > 0 ? { linkId: raw.linkId } : {};
  return { status: raw.status, entity: parseSection(raw.entity), individual, ...linkId };
};

// ---- DOM personalization via the `data-bryn-*` attribute vocabulary (BRYN-594) ----
//
// One learnable pattern rather than a documented attribute per field: every marker's
// value is a **bryn path** into the serve payload. The grammar (dot-separated):
//
//   [entity.|individual.]<attribute>     an attribute string  e.g. entity.companyName, firstName
//   [entity.|individual.]signals         the active-signal list for that level
//   [entity.|individual.]signals.<type>  membership — is <type> an active signal at that level
//   status                               the resolution status ("pending"|"resolved"|"suppressed")
//
// A bare (unprefixed) path searches both sections, individual first. Three attributes
// consume a path:
//
//   data-bryn-field="<path>"       set the element's text to the string the path resolves to
//   data-bryn-show="<condition>"   show the element only while the condition holds
//   data-bryn-hide="<condition>"   hide the element while the condition holds
//
// A <condition> is a bare path (present / non-empty / a true membership ⇒ true) or
// `<path>=<value>` (equality: a string match, or list membership). Show/hide toggle the
// standard `hidden` attribute, so the host authors the default state in markup and the
// beacon reveals or hides it once identity resolves (progressive enhancement).

const FIELD_ATTR = "data-bryn-field";
const SHOW_ATTR = "data-bryn-show";
const HIDE_ATTR = "data-bryn-hide";
// Search order for an unprefixed path: individual before entity, so the person wins any
// key collision with the company (matches the pre-BRYN-933 "individual renders last" rule).
const SEARCH_SECTIONS = ["individual", "entity"] as const;

/** Pick a named section off the payload; `individual` may be null. */
const sectionOf = (payload: ServePayload, name: string): ServeSection | null =>
  name === "entity" ? payload.entity : name === "individual" ? payload.individual : null;

/** A string display attribute (never the reserved `signals` list) off one section, or null. */
const attributeOf = (section: ServeSection, key: string): string | null => {
  if (key === "signals") return null;
  const value = section[key];
  return typeof value === "string" ? value : null;
};

// A resolved bryn path: a string attribute (`value`, also `status`), a signals list, or a
// signal-membership boolean. `null` from resolvePath means malformed / points at nothing.
type PathResult =
  | { kind: "value"; value: string }
  | { kind: "list"; list: string[] }
  | { kind: "bool"; value: boolean };

/**
 * Resolve a bryn path against the payload. Reading (never writing) payload fields by key,
 * with the prototype-key guard on every segment, keeps a crafted path (`__proto__`, …)
 * from traversing object internals.
 */
export const resolvePath = (payload: ServePayload, path: string): PathResult | null => {
  const segments = path.split(".");
  if (!segments.every(isSafePathSegment)) return null;

  // Top-level `status` — a scalar, so only the bare `status` path.
  if (segments[0] === "status")
    return segments.length === 1 ? { kind: "value", value: payload.status } : null;

  // Optional section prefix; otherwise search both sections (individual first).
  const scoped = segments[0] === "entity" || segments[0] === "individual";
  const rest = scoped ? segments.slice(1) : segments;
  if (rest.length === 0 || rest.length > 2) return null;
  const names: readonly string[] = scoped ? [segments[0]] : SEARCH_SECTIONS;

  if (rest[0] === "signals") {
    if (rest.length === 2) {
      const type = rest[1];
      const present = names.some((n) => sectionOf(payload, n)?.signals.includes(type) ?? false);
      return { kind: "bool", value: present };
    }
    const list: string[] = [];
    for (const n of names) list.push(...(sectionOf(payload, n)?.signals ?? []));
    return { kind: "list", list };
  }

  if (rest.length !== 1) return null; // an attribute is a single trailing segment
  for (const n of names) {
    const section = sectionOf(payload, n);
    const value = section && attributeOf(section, rest[0]);
    if (value != null) return { kind: "value", value };
  }
  return null;
};

/** The string a `data-bryn-field` path resolves to, or null (then leave the host default). */
export const resolveFieldValue = (payload: ServePayload, path: string): string | null => {
  const result = resolvePath(payload, path);
  return result?.kind === "value" ? result.value : null;
};

/**
 * Evaluate a `data-bryn-show` / `data-bryn-hide` condition. A bare `<path>` is a presence
 * test (non-empty string, non-empty list, or a true membership); `<path>=<value>` is an
 * equality test (string match or list membership). Malformed / missing ⇒ false.
 */
export const evalCondition = (payload: ServePayload, condition: string): boolean => {
  const eq = condition.indexOf("=");
  const path = (eq === -1 ? condition : condition.slice(0, eq)).trim();
  const expected = eq === -1 ? null : condition.slice(eq + 1);
  const result = resolvePath(payload, path);
  if (!result) return false;
  if (expected === null) {
    if (result.kind === "value") return result.value.length > 0;
    if (result.kind === "list") return result.list.length > 0;
    return result.value;
  }
  if (result.kind === "value") return result.value === expected;
  if (result.kind === "list") return result.list.includes(expected);
  return result.value === (expected === "true");
};

/** Toggle the standard `hidden` attribute — XSS-free, CSS-overridable, no inline style. */
const setHidden = (el: ElementLike, hidden: boolean): void => {
  if (hidden) el.setAttribute("hidden", "");
  else el.removeAttribute("hidden");
};

/**
 * Apply `fn` to every element carrying `marker`, reading the marker's own value. Errors are
 * swallowed per element (one bad attribute never breaks the rest) and per family (an
 * unsupported/throwing `queryAll` never breaks the page).
 */
const eachMarked = (
  env: PixelEnv,
  marker: string,
  fn: (el: ElementLike, value: string) => void,
): void => {
  if (!env.queryAll) return;
  try {
    const nodes = env.queryAll(`[${marker}]`);
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      const value = el.getAttribute(marker);
      if (value == null) continue;
      try {
        fn(el, value);
      } catch {
        // One malformed marker must not break personalization for the others.
      }
    }
  } catch {
    // queryAll unsupported or threw — skip this family, never break the page.
  }
};

// The host's original field text, captured per element the first time we personalize it.
// Continuous polling (BRYN-1008) can apply a terminal `suppressed` after a `resolved` — the
// "stop showing / don't leak" path — or a later payload that drops a field. In both cases
// the field path stops resolving, and without the captured default we would leave stale
// personalized text on screen. Keyed by element identity, so it never mutates host markup.
const fieldDefaults = new WeakMap<ElementLike, string>();

/**
 * Apply a payload to the host DOM through the `data-bryn-*` vocabulary: inject field text,
 * then toggle `data-bryn-show` / `data-bryn-hide` elements. A no-op when the host wired no
 * `queryAll` (publisher-only installs).
 */
const applyDom = (env: PixelEnv, payload: ServePayload): void => {
  eachMarked(env, FIELD_ATTR, (el, path) => {
    const value = resolveFieldValue(payload, path);
    if (value != null) {
      // Capture the host default once, before the first overwrite, so it can be restored.
      if (!fieldDefaults.has(el)) fieldDefaults.set(el, el.textContent ?? "");
      el.textContent = value;
    } else {
      // No value resolves for this path (a suppressed payload, or a field dropped from a
      // later one): restore the host default rather than leaving stale personalized text.
      const original = fieldDefaults.get(el);
      if (original !== undefined) el.textContent = original;
    }
  });
  eachMarked(env, SHOW_ATTR, (el, condition) => setHidden(el, !evalCondition(payload, condition)));
  eachMarked(env, HIDE_ATTR, (el, condition) => setHidden(el, evalCondition(payload, condition)));
};

/** Every field path a payload carries, mapped to its serialized value, in `data-bryn-field` grammar. */
const fieldValues = (payload: ServePayload): Map<string, string> => {
  const values = new Map<string, string>([["status", JSON.stringify(payload.status)]]);
  if (payload.linkId !== undefined) values.set("linkId", JSON.stringify(payload.linkId));
  const sections: [string, ServeSection | null][] = [
    ["entity", payload.entity],
    ["individual", payload.individual],
  ];
  for (const [name, section] of sections) {
    if (!section) continue;
    // A section's `signals` list is one path, compared in order: the change gate in
    // `applyIfChanged` is a JSON comparison, so a reorder is an applied change too.
    for (const key of Object.keys(section)) values.set(`${name}.${key}`, JSON.stringify(section[key]));
  }
  return values;
};

/**
 * The field paths that differ between the previously applied payload and this one, for
 * `bryn:updated`. A path counts as changed when its value differs, appears, or disappears
 * (a field the tenant turned off is listed, and is absent from the payload). Paths are always
 * leaves: `individual` going from null to a person lists each of the person's fields. With no
 * previous payload (the first one this page view applies) every present field is listed.
 * A payload that differs from the previous one only in key order is applied (the change gate
 * compares JSON) and lists nothing; serve projects keys in a fixed order, so it doesn't arise.
 */
export const changedFieldPaths = (previous: ServePayload | undefined, next: ServePayload): string[] => {
  const before = previous ? fieldValues(previous) : new Map<string, string>();
  const after = fieldValues(next);
  const changed = [...after].filter(([path, value]) => before.get(path) !== value).map(([path]) => path);
  const removed = [...before.keys()].filter((path) => !after.has(path));
  return [...changed, ...removed];
};

/**
 * Surface a retrieved payload three ways (spec §17.4): apply it to the DOM via the
 * `data-bryn-*` attribute vocabulary (field text + show/hide — see `applyDom`), publish
 * the latest payload to the `window.bryn` DOM global for synchronous reads (decision 4a),
 * and dispatch a `bryn:personalize` CustomEvent (observer mode), followed by `bryn:updated`
 * (BRYN-2056) with the payload and the field paths that changed since `previous`. All fire on
 * every applied payload — the observer and global paths are what let the page upgrade after
 * load (decision 2026-06-23).
 */
export const applyServePayload = (env: PixelEnv, payload: ServePayload, previous?: ServePayload): void => {
  // Diffed before any host code sees the payload: a `bryn:personalize` listener (or a
  // `window.bryn` setter) gets this same object and could otherwise hide or fake a change.
  const changedKeys = changedFieldPaths(previous, payload);
  applyDom(env, payload);
  // Best-effort: a host page that already defines `window.bryn` as a non-writable or
  // accessor property would throw on assignment. Isolate the new sink so a global-name
  // collision can't break the event path below or the poll/cache flow in startRetrieve.
  try {
    env.publish?.(payload);
  } catch {
    // Non-fatal — the render and event surfaces still fire.
  }
  // The `dispatch` closure builds a `CustomEvent` and calls `dispatchEvent` on the host's
  // `globalThis`. Both can throw on a hostile override — `new CustomEvent(type, init)` rejects
  // bad init shapes, `dispatchEvent` itself throws when called as a non-function. The whole
  // point of the beacon is to NEVER take the page down with it, so the dispatch is isolated
  // even though the DOM path and the `publish` sink above are already wrapped.
  if (env.dispatch) {
    try {
      env.dispatch("bryn:personalize", payload);
    } catch {
      // Non-fatal — the DOM personalization and the `window.bryn` sink already fired.
    }
    // Its own try, so a throw here (or from the `bryn:personalize` dispatch above) leaves the
    // other event intact. Same point in the flow, so listeners on either event still run
    // before the poll loop's PostHog write (see `createPosthogSync`).
    try {
      env.dispatch("bryn:updated", { payload, changedKeys });
    } catch {
      // Non-fatal — `bryn:personalize`, the DOM and `window.bryn` are unaffected.
    }
  }
};

// ---- PostHog correlation, on by default (BRYN-1601) ----
//
// Surfacing the resolution on the page (above) and leaving the analytics write to a snippet
// the tenant has to author meant that, out of the box, NOTHING was correlated: on civic.com, 0
// of the single-page landing visits carried any `bryn` data in PostHog. And with nothing on the
// PostHog person, the return leg has nothing to echo back — `bryn.*` signal-mapping paths and
// the `linkId` correction bridge (BRYN-1382) both stay inert. So the beacon now performs the
// write itself when the host page runs posthog-js.
//
// `setPersonProperties`, never `identify()`: it attaches Bryn's data to whoever PostHog already
// thinks the visitor is without touching `distinct_id`, so it cannot clobber a tenant's own
// identification. This is the same call Bryn's own app makes
// (`apps/control-plane-ui/src/lib/analytics/bryn-stitch.ts`). The payload is forwarded WHOLE
// under one `bryn` key — filtering it to an allowlist here would silently disarm the correction
// bridge, which reads `bryn.linkId` back off the forwarded event.
//
// Three PostHog behaviours this design rests on, verified against their docs and, where the docs
// are silent, against the shipped posthog-js source rather than assumed:
//
//   1. `setPersonProperties` CREATES a person profile even under the default
//      `person_profiles: "identified_only"` — https://posthog.com/docs/data/persons lists it as
//      one of three ways a profile is created, and posthog-js implements its own
//      `createPersonProfile()` AS a `setPersonProperties({}, {})` call. So a company-only
//      resolution on an anonymous visitor lands.
//   2. It creates an ANONYMOUS person, not an identified one. Only `identify()` writes
//      `$user_state = "identified"`; `setPersonProperties` leaves it `"anonymous"`, so a later
//      `identify()` still takes its full path and fires `$identify` with `$anon_distinct_id` —
//      the backend then merges our person into theirs. This is what makes the write safe to
//      perform before the tenant has identified anyone.
//   3. That merge keeps "the surviving (identified) person's value" on a key both hold
//      (https://posthog.com/docs/product-analytics/identify). So a returning visitor whose
//      identified person already carries a STALE `bryn` would keep the stale one. `flush`
//      therefore re-writes whenever the distinct id changes under it, which is exactly the
//      post-identify moment.
//
// ## Cost: this changes the tenant's PostHog bill
//
// Read this before widening or narrowing the write condition, because it is the only real
// argument about where that condition belongs.
//
// PostHog prices anonymous events below identified ones — "anonymous events can be up to 4x
// cheaper than identified ones" (https://posthog.com/docs/data/anonymous-vs-identified-events).
// An event is identified when it carries `$process_person_profile: true`.
//
// A `setPersonProperties` call does not just make ITS OWN `$set` event identified. In posthog-js
// it routes through `_requirePersonProcessing`, which registers a PERSISTED `$epp: true` super
// property; `_hasPersonProcessing()` reads that back, and `_calculate_event_properties` stamps
// `$process_person_profile` onto every event from then on. The flag lives in the browser's
// persistence, so it survives reloads and later visits until site data is cleared.
//
// So one write moves that browser's entire event stream — past this point, and in future
// sessions — onto identified pricing. Writing on any payload that names an account (the
// condition above) means that happens for close to every pixel visitor, including ones who never
// resolve to anything useful. That is a deliberate v1 decision: correlation coverage was judged
// worth the cost, and the cost is bounded by three things rather than by the write condition —
// `Tenant.pixelPosthogSyncEnabled`, the `PIXEL_POSTHOG_SYNC` operator kill switch, and
// POSTHOG_MAX_WRITES below. Do not remove those without re-opening this trade-off.

/**
 * The posthog-js surface the correlation write needs, modelled structurally (the pixel is an
 * import-free bundle, so it cannot depend on posthog-js types). Everything optional: the host's
 * `window.posthog` is untrusted and may be a wrapper, an older version, or the loader stub.
 */
type PosthogLike = {
  setPersonProperties?: (properties: Record<string, unknown>) => void;
  get_distinct_id?: () => unknown;
  on?: (event: string, handler: (payload: unknown) => void) => unknown;
};

/** A `window.posthog` that can actually take the write. */
type PosthogWithWrite = PosthogLike & {
  setPersonProperties: (properties: Record<string, unknown>) => void;
};

/**
 * Serve-response header by which the SERVER authorises the PostHog write, per request.
 *
 * Deliberately opt-in (`on`), not opt-out (`off`): the pixel writes into the tenant's own
 * analytics account, and it is a CDN-cached asset that updates independently of the API, so
 * "absent header ⇒ write" would mean a stale pixel against an older API, or any
 * `Access-Control-Expose-Headers` regression, writes for tenants who switched it off. Opt-in
 * fails the safe way in every ordering: the worst case is the correlation not happening, which
 * is the status quo this ticket improves on, not an unwanted write into someone's PostHog.
 *
 * A header rather than a payload field because the payload is echoed verbatim into PostHog —
 * Bryn's own config has no business riding into a customer's person properties.
 */
const POSTHOG_SYNC_HEADER = "X-Bryn-Posthog-Sync";
const POSTHOG_SYNC_ON = "on";

/**
 * Serve-response header by which the SERVER tells the beacon to stop its return-leg poll
 * (BRYN-1960, collection-only mode). Sent with the value {@link RETURN_LEG_OFF} when
 * `Tenant.pixelReturnLegEnabled` is `false`; omitted otherwise.
 *
 * Opt-in by design, the same reasoning as {@link POSTHOG_SYNC_HEADER}: the pixel is CDN-cached
 * and updates independently of the API, so a missing header MUST mean "keep polling" (today's
 * behaviour) — a stale asset against a newer API must fail safe, and a tenant who switches the
 * return leg on is silently honoured without waiting out a cache. Reaching the beacon at all
 * depends on the serve leg naming it in `Access-Control-Expose-Headers` (it is cross-origin);
 * without that entry, the browser hides the header and the switch silently no-ops.
 */
const RETURN_LEG_HEADER = "X-Bryn-Return-Leg";

/** The only value the beacon treats as "return leg off". */
const RETURN_LEG_OFF = "off";

/**
 * Whether this serve response is telling the beacon to end its return-leg poll loop. Mirrors
 * {@link posthogSyncAllowed}'s guards: a missing `headers` accessor, a missing header, or a
 * throwing one all read as "keep polling", so a missing header is the safe default and the
 * beacon needs the explicit `off` value to stop. Trimmed and case-insensitive on the value
 * only, since servers and proxies are free to fold header values however they like.
 */
export const returnLegOff = (res: ServeResponseLike): boolean => {
  try {
    return res.headers?.get(RETURN_LEG_HEADER)?.trim().toLowerCase() === RETURN_LEG_OFF;
  } catch {
    return false;
  }
};

/**
 * Whether this serve response authorises the PostHog write. Requires the explicit `on` (see
 * {@link POSTHOG_SYNC_HEADER}); a missing `headers` accessor, a missing header, or a throwing
 * one all read as "not authorised". NOTE: the header only reaches this code because the serve
 * route names it in `Access-Control-Expose-Headers` — the beacon request is cross-origin, and a
 * browser hides every non-safelisted response header from JS otherwise. Nothing on either side
 * reports an error if that is dropped; the correlation just stops happening.
 */
export const posthogSyncAllowed = (res: ServeResponseLike): boolean => {
  try {
    return res.headers?.get(POSTHOG_SYNC_HEADER)?.trim().toLowerCase() === POSTHOG_SYNC_ON;
  } catch {
    return false;
  }
};

/**
 * Ceiling on `$set` writes for one page view. Each write is a billable PostHog event, and the
 * write can feed itself: a tenant's unfiltered PostHog destination forwards our `$set` back into
 * Bryn ingest, where it is an ordinary inbound Event that can move the served payload, which
 * would move us to write again. The changed-payload/changed-identity guards below already make
 * that converge; this is the backstop that bounds it even if they don't.
 */
const POSTHOG_MAX_WRITES = 8;

/**
 * Read the host page's posthog-js instance, or undefined when there is nothing to write to
 * (yet). Called on every poll, so "not yet" is simply retried for as long as the loop runs.
 *
 * `isRecord` rejects arrays, which also and deliberately skips PostHog's own loader stub: the
 * HTML snippet installs `window.posthog = window.posthog || []` and hangs queueing methods off
 * that array until `array.js` lands. Writing through the stub would work (the queue is replayed)
 * but `get_distinct_id()` on it returns undefined, so we would not know which person we wrote to
 * and would spend a second `$set` learning. Waiting for the real instance costs one poll.
 */
const readPosthog = (env: PixelEnv): PosthogWithWrite | undefined => {
  if (!env.posthog) return undefined;
  try {
    const candidate = env.posthog();
    if (!isRecord(candidate)) return undefined;
    // The property READ is inside the try, not just the global access: `window.posthog` may be a
    // Proxy or an accessor-based wrapper, and reading a key off one of those can throw on its own.
    return typeof candidate.setPersonProperties === "function"
      ? (candidate as PosthogWithWrite)
      : undefined;
  } catch {
    // Reading the host global, or a key off it, threw — never break the page over it.
    return undefined;
  }
};

/**
 * The current PostHog person id, or undefined when it can't be read (wrapper, stub, throw).
 *
 * `get_distinct_id()` rather than `_isIdentified()`, which posthog-js also exposes. Two reasons:
 * the underscore is theirs, not ours — it is absent from the loader snippet's own method list and
 * is not part of a contract they support — and "is this person identified?" is not the question
 * that decides anything here. The write is safe either way (`setPersonProperties` never touches
 * `distinct_id`); what we need to know is WHICH person we last wrote to, so we can write again
 * when it changes. That is the id, not a boolean.
 */
const readDistinctId = (posthog: PosthogLike): string | undefined => {
  try {
    const id = posthog.get_distinct_id?.();
    return typeof id === "string" && id.length > 0 ? id : undefined;
  } catch {
    return undefined;
  }
};

/** PostHog events that mean "the person under us changed", so the `bryn` property must be re-set. */
const IDENTITY_EVENTS = new Set(["$identify", "$create_alias"]);

/**
 * Whether a payload carries anything worth correlating — the write condition (BRYN-1601,
 * decision 2026-09-09), replacing the original `status === "resolved"` gate.
 *
 * A `pending` payload counts as soon as the visitor's Entity exists: `entity.entityId` (and
 * `domain`) travel on it (BRYN-1393), and that window — attributable to an account, not yet
 * enriched — is exactly where their PostHog events would otherwise look like a separate person.
 * Waiting for `resolved` threw it away.
 *
 * How often that window is actually hit depends on the tenant: the serve leg counts one active
 * Signal as displayable, so a tenant whose pixel events map to a signal reports `resolved` on the
 * same beacon that creates the Entity and never passes through pending-with-an-id. This condition
 * is therefore about not LOSING the visitors who do sit in that window (a husk under a tenant with
 * no pixel signal mapping; a returning visitor whose identity cache lapsed), not about a second
 * write everyone gets.
 *
 * Expressed as "not the cold envelope" rather than "has an `entityId`", deliberately. The server
 * returns `emptySection()` — exactly `{ signals: [] }` — for a cold visitor and for a terminal
 * `suppressed`, and anything else it returns names the visitor somehow. Keying on one field name
 * would make the whole correlation hinge on that field surviving every future payload change, and
 * a server that stopped sending it would silently stop the write for RESOLVED visitors too — the
 * regression this ticket exists to fix. Counting keys cannot fail that way.
 *
 * `suppressed` is excluded explicitly as well as structurally: it is the "stop showing, do not
 * leak" state, and that intent should not depend on the section happening to be empty.
 */
const carriesIdentity = (payload: ServePayload): boolean => {
  if (payload.status === "suppressed") return false;
  if (payload.individual !== null) return true;
  // More than the reserved `signals` key, or a non-empty signal list ⇒ not the cold envelope.
  return Object.keys(payload.entity).length > 1 || payload.entity.signals.length > 0;
};

/**
 * The correlation sink for one page. TWO methods, and the split is load-bearing: they belong on
 * either side of the `bryn:personalize` dispatch, and three separate bugs came from getting that
 * wrong.
 *
 * **Why the dispatch is a hazard at all.** Applying a payload dispatches `bryn:personalize`; a
 * tenant listener on that event commonly calls `posthog.identify()` (Recipe 7 suggests exactly
 * that); and dispatch -> their listener -> PostHog's `$identify` -> our `eventCaptured` hook ->
 * `flush` is entirely SYNCHRONOUS. So the dispatch is a re-entry point into this sink, on the
 * host page's terms, in the middle of our own poll handling.
 *
 * `receive(payload, allowed)` therefore runs BEFORE the payload is applied, and it is where every
 * "stop" decision is made — both the response withdrawing authorisation and the payload being a
 * terminal `suppressed`. Deciding either one after the dispatch means it is not in effect for the
 * re-entry the dispatch itself causes: the stale held resolution gets written from inside the very
 * payload that was meant to stop it.
 *
 * `write(payload)` runs AFTER, which is equally deliberate: a tenant's `identify()` goes first, so
 * our write lands last, on the person they just established.
 *
 * Neither is a caller-side `if`, because the sink outlives any one response — it holds the last
 * payload and a listener that fires on the host page's schedule, so a "stop" has to reach it
 * rather than be skipped. And do not merge the two calls: a single entry point cannot be both
 * before and after the dispatch, which is the whole point.
 *
 * `stop()` is the third method and covers the case neither of the others can: the poll loop
 * ENDING. Authorisation is per response, so it is only ever as fresh as the last poll — and the
 * listener outlives the loop. Whenever polling stops (the pending-attempt ceiling, or a terminal
 * payload) the verdict can never be re-evaluated again, so the sink has to stand down instead of
 * holding a listener that would keep writing whatever it last held for the rest of the tab's
 * life. Every `return` that ends the loop must call it.
 *
 * ## The bound on the sink's authority, in one place
 *
 * Four ordering/lifetime bugs came out of this sink holding state that outlives whatever
 * authorised it, so the boundaries are enumerated here rather than left to be rediscovered:
 *
 * | Authority ends when… | Bounded by |
 * | -------------------- | ---------- |
 * | the response's verdict changes | `receive`, called per response before anything is applied |
 * | the dispatch re-enters us mid-poll | the receive/write split around `applyIfChanged` |
 * | the write re-enters us via a wrapper | the `flushing` re-entrancy flag |
 * | the poll loop ends | `stop()`, at every loop exit |
 *
 * Two `startRetrieve` exits need nothing because the sink does not exist yet at that point (no
 * `serveFetch`/`setTimeout`, and the cached-terminal short circuit, both of which return before
 * `createPosthogSync`). `write` can never run without a preceding `receive`: they are adjacent in
 * the one branch that calls them.
 *
 * KNOWN, ACCEPTED LIMIT: if the host's `setTimeout` never fires — a page that stubs it out, or a
 * tab discarded and never resumed — polling stops without reaching either loop exit, so the sink
 * keeps the last verdict indefinitely. Deliberately not guarded: a broken `setTimeout` means no
 * polling at all, hence no personalization either, and from inside the page "not yet fired" is
 * indistinguishable from "never will".
 */
export type PosthogSync = {
  readonly receive: (payload: ServePayload, allowed: boolean) => void;
  readonly write: (payload: ServePayload) => void;
  readonly stop: () => void;
};

/**
 * Build the PostHog correlation sink for one page: call it with every payload the poll retrieves
 * and it writes `{ bryn: payload }` to the visitor's PostHog person when there is something new
 * to say. Stateful per page, so it is created once per {@link startRetrieve}.
 *
 * It writes when, and only when:
 * - the payload carries something worth correlating ({@link carriesIdentity}) — any `status`,
 *   any `individual`. A `pending` payload counts as soon as the visitor's Entity exists, which is
 *   the window in which their PostHog events would otherwise look like a separate person.
 *   Company-only resolutions count too, and are the majority of reverse-IP results. Only the cold
 *   envelope and a terminal `suppressed` are excluded;
 * - a `pending` payload is not arriving after a resolution (that would downgrade the person's
 *   `bryn` from a company with attributes back to a bare id);
 * - posthog-js is actually on the page (retried on each poll until it is);
 * - the payload changed since our last write, or the distinct id under us changed (a tenant
 *   `identify()` landed — see the merge-precedence note above), or nothing has been written yet;
 * - the per-page write ceiling has not been reached.
 *
 * Every PostHog call is wrapped: a host page's posthog wrapper that throws must not take the
 * beacon's poll loop or DOM personalization down with it.
 */
export const createPosthogSync = (env: PixelEnv): PosthogSync => {
  let latest: ServePayload | undefined;
  let writtenJson: string | undefined;
  let writtenDistinctId: string | undefined;
  let writes = 0;
  let hooked = false;
  /** Teardown for the `eventCaptured` listener, when posthog-js handed one back. */
  let unhook: (() => void) | undefined;
  /**
   * Re-entrancy guard for {@link flush}.
   *
   * `setPersonProperties` is host-page code. A wrapper that identifies inside it would drive
   * PostHog's `$identify` -> our `eventCaptured` hook -> `flush` again, synchronously, from
   * INSIDE the write — and at that point `writtenJson`/`writtenDistinctId` have not been updated
   * yet (deliberately: they are set after the call returns, so a throwing write is retried rather
   * than recorded as done). The nested flush therefore sees fresh work and writes again.
   *
   * POSTHOG_MAX_WRITES does NOT bound this, which was the assumption worth checking: `writes` is
   * incremented only after the nested call returns, so the ceiling is still reading zero all the
   * way down. Measured by removing this flag: 1,897 writes before the stack ran out — the depth
   * limit was the only thing stopping it, and each frame's `catch` then swallowed the RangeError
   * on the way back up, so nothing surfaced. That is ~1,900 billable PostHog events for one
   * resolution, from inside a customer's page. This flag is the actual bound.
   */
  let flushing = false;
  /** Whether the MOST RECENT serve response authorised the write. Never assumed. */
  let authorized = false;
  // Mirrors the poll loop's own `resolvedSeen`: once a resolution has been written, a later
  // `pending` must not downgrade the person's `bryn` back to a bare id.
  let resolvedSeen = false;

  /**
   * Re-sync as soon as a tenant `identify()` lands, rather than on the next 20s poll. Cheap
   * insurance: a visitor who identifies and closes the tab inside one poll interval would
   * otherwise keep whatever stale `bryn` their identified person already had. Registered once,
   * the first time we see a real instance. `flush` filters on the event NAME, so the `$set` our
   * own write emits is ignored and the hook cannot drive itself.
   */
  const hookIdentity = (posthog: PosthogLike): void => {
    if (hooked || typeof posthog.on !== "function") return;
    hooked = true;
    try {
      const off = posthog.on("eventCaptured", (event) => {
        // This body runs inside PostHog's OWN event dispatch, so an uncaught throw here would
        // break their pipeline for the host page — the one thing the beacon must never do. Both
        // halves can throw independently: reading `event.event` off a wrapper's Proxy, and
        // `flush` itself if a future edit adds an unguarded call to it.
        try {
          const name = isRecord(event) ? event.event : undefined;
          if (typeof name === "string" && IDENTITY_EVENTS.has(name)) flush();
        } catch {
          // Swallowed: the poll still catches the identity change on its next tick.
        }
      });
      // posthog-js returns an unsubscribe; older builds and wrappers may not.
      if (typeof off === "function") unhook = off as () => void;
    } catch {
      // Hook unsupported on this posthog build — the poll still catches the id change.
    }
  };

  /**
   * Stand down completely: forget the held payload and remove the listener from the host's
   * posthog instance. Called when authorisation is withdrawn and on a terminal `suppressed`.
   *
   * Both of those mean "write nothing further", and the listener is the reason that has to be an
   * action rather than just not calling in again — it lives on the tenant's own posthog object
   * and would otherwise keep calling `flush` for the tab's lifetime. `hooked` is reset so a
   * later re-authorisation re-registers rather than running blind.
   */
  const disarm = (): void => {
    latest = undefined;
    try {
      unhook?.();
    } catch {
      // Best effort — an unsubscribe that throws must not break the poll loop.
    }
    unhook = undefined;
    hooked = false;
  };

  const flush = (): void => {
    const payload = latest;
    // `authorized` is re-checked here, not only at the entry point: `flush` is also reached from
    // the `eventCaptured` listener, which fires on the host page's schedule rather than ours.
    if (!authorized || !payload || writes >= POSTHOG_MAX_WRITES || flushing) return;
    const posthog = readPosthog(env);
    if (!posthog) return; // retried on the next poll
    hookIdentity(posthog);

    const distinctId = readDistinctId(posthog);
    const json = JSON.stringify(payload);
    // An id we have never seen is not a change: it is the first read, or the first read after a
    // write we made before the id was legible. Only a DIFFERENT known id means the person moved.
    const identityChanged =
      writtenDistinctId !== undefined &&
      distinctId !== undefined &&
      distinctId !== writtenDistinctId;
    if (json === writtenJson && !identityChanged) {
      writtenDistinctId ??= distinctId;
      return;
    }

    flushing = true;
    try {
      posthog.setPersonProperties({ bryn: payload });
    } catch {
      // Leave the state untouched so the next poll retries this same write.
      return;
    } finally {
      flushing = false;
    }
    writes += 1;
    writtenJson = json;
    writtenDistinctId = distinctId ?? writtenDistinctId;
  };

  return {
    receive: (payload, allowed) => {
      authorized = allowed;
      // BOTH stand-down reasons are decided here, before the page is told anything:
      //
      //   !allowed              the tenant switched the correlation off, or the operator kill
      //                         switch went in
      //   status "suppressed"   terminal, the server's "stop showing, do not leak"
      //
      // Each means "write nothing further", and each has to take effect before the dispatch this
      // response is about to trigger, because that dispatch can re-enter `flush` synchronously
      // via a tenant `identify()` listener. Deciding either one afterwards writes the stale held
      // resolution from inside the very payload that was meant to stop it.
      //
      // (Removing a `bryn` property already written is out of scope — this stops adding to it.)
      if (!allowed || payload.status === "suppressed") disarm();
    },

    stop: () => {
      // The poll loop has ended, so there will be no further authorisation verdict — ever. Drop
      // the held payload and detach the listener rather than leave it able to write on a verdict
      // that can no longer be refreshed. Idempotent, so every loop exit can call it.
      authorized = false;
      disarm();
    },

    write: (payload) => {
      // Re-checked, so `write` is safe to call regardless of what the caller did.
      if (!authorized) return;
      // Nothing to correlate against: a cold visitor's `pending` payload names nobody, and
      // writing it would create a billable PostHog person carrying only `status: "pending"`.
      // `carriesIdentity` also excludes `suppressed`, which `receive` has already disarmed on.
      if (!carriesIdentity(payload)) return;
      // Once a resolution has been written, ignore a later `pending` — it is routinely a
      // transient serve-error fallback, and overwriting the person's `bryn` with a bare id would
      // lose the company attributes it already holds. Mirrors the poll loop's downgrade guard.
      if (payload.status === "resolved") resolvedSeen = true;
      else if (resolvedSeen) return;
      latest = payload;
      flush();
    },
  };
};

/**
 * Diagnostics outcome for a failed serve response. A non-2xx status is reported as itself, so
 * support can tell a 429 from a 503 from a 404. `network-error` covers the rest: status `0`
 * (the host's fetch surfaced no real status) and a 2xx whose body was not our payload.
 */
const serveFailureOutcome = (status: number): "network-error" | number =>
  status === 0 || (status >= 200 && status < 300) ? "network-error" : status;

/** First-party session cache key for a serve URL, so later pages don't re-retrieve. */
const cacheKey = (serveUrl: string): string => `bryn:serve:${serveUrl}`;

/**
 * Begin the return leg: apply a cached `suppressed` resolution if present, else poll the
 * serve endpoint with backoff that settles into a steady cadence and keeps polling
 * (BRYN-1008), so late-arriving enrichment and Signals continue to flow. Each response is
 * applied (the page shows the library default first, then upgrades on every poll); only a
 * terminal `suppressed` payload is cached and stops the loop. A `resolved` payload is
 * applied but never stops polling on its own. A no-op when the host wired neither `serveFetch`
 * nor `setTimeout`.
 *
 * The loop is bounded (BRYN-1959):
 *  - Until the first `resolved` payload is seen, capped at `RETRIEVE_MAX_PENDING_ATTEMPTS`
 *    polls so a never-resolving visitor stops instead of polling for the tab's lifetime.
 *  - In either phase, `RETRIEVE_MAX_CONSECUTIVE_FAILURES` consecutive failures (rejections or
 *    `ok: false`) end the loop — a serve endpoint that's gone dark does not get better with
 *    more requests.
 *  - In the resolved phase, `RETRIEVE_RESOLVED_IDLE_MS` of unchanged payload ends the loop;
 *    a `visibilitychange` while idle re-arms one poll, then the idle window restarts.
 *  - While the tab is hidden, due polls are parked (not fetched) and the parked poll runs
 *    immediately on becoming visible. A parked poll does not count as an attempt and does
 *    not extend the pending window.
 *
 * Each response also feeds the PostHog correlation sink when the response authorises it
 * (BRYN-1601) — see {@link createPosthogSync} and {@link posthogSyncAllowed}. Every loop
 * exit calls `syncPosthog.stop()`, the invariant from the {@link createPosthogSync} doc
 * comment: the listener outlives the loop, so a stopped loop must stand the sink down.
 *
 * The serve response can additionally carry `X-Bryn-Return-Leg: off`
 * (BRYN-1960, collection-only mode): the response-time mirror of the
 * `data-bryn-personalize="off"` snippet opt-out, set when
 * `Tenant.pixelReturnLegEnabled` is `false`. The beacon applies the payload (so a
 * `pending` already in flight still informs the page once) and ends the loop on that
 * response (stop reason `collection-only`), so a return-leg-off tenant's visitors stop
 * polling after the first request
 * instead of running the full pending window. The correlation sink is disarmed with
 * the loop, because the verdict can never be re-checked and a stale `bryn` would
 * still write on the next `identify()`.
 */
export const startRetrieve = (env: PixelEnv, serveUrl: string): void => {
  if (!env.serveFetch || !env.setTimeout) return;
  const fetchServe = env.serveFetch;
  const schedule = env.setTimeout;
  // The diagnostics publisher (BRYN-1962) is optional — a host that does not need the
  // customer-support surface simply leaves `env.diagnostics` undefined and nothing is
  // computed or written. Every transition below is gated on its presence, so the loop
  // stays byte-identical to the pre-PR behaviour for hosts that opt out.
  const diag = env.diagnostics;

  // Apply a payload only when it differs from the last one applied (BRYN-1008). The poll
  // now runs for the tab's lifetime, so re-applying an unchanged payload would re-fire
  // `bryn:personalize` (and its listener side effects, e.g. posthog.identify/group) and
  // rewrite the DOM every cycle. Tracking the last-applied JSON keeps continuous polling
  // cheap and side-effect-free until the served data actually changes. `applyIfChanged` also
  // returns whether it actually applied — the resolved-phase idle ceiling keys off that, not
  // off the polling clock, because what "still useful" means is whether the served payload
  // moved, not whether the timer fired.
  let lastAppliedJson: string | undefined;
  let lastAppliedAt: number | undefined;
  const applyIfChanged = (payload: ServePayload): boolean => {
    const json = JSON.stringify(payload);
    if (json === lastAppliedJson) return false;
    // `bryn:updated` diffs against the last payload THIS page view applied, rebuilt from the
    // JSON snapshot rather than the object listeners received, so a listener mutating its
    // `detail` cannot skew the next diff. The cached suppressed payload restored below is the
    // page's first applied payload, so it lists every field like any other first payload.
    const previous = lastAppliedJson === undefined ? undefined : parseServePayload(JSON.parse(lastAppliedJson));
    lastAppliedJson = json;
    // `lastAppliedAt` is the resolved-phase idle ceiling's clock; the cached-terminal path
    // below returns immediately, so updating it there is wasted work and a needless dependency
    // on `env.now` for a host that wires only the cache leg. Only stamp the clock when the loop
    // is actually continuing to poll.
    lastAppliedAt = env.now?.();
    applyServePayload(env, payload, previous ?? undefined);
    return true;
  };

  // Apply a cached terminal (suppressed) payload immediately and skip polling entirely. The
  // `getItem` read sits inside the same try as the parse: a throwing read (blocked site data,
  // a private window, a sandboxed iframe) and a corrupt blob both fall through to fresh
  // polling — never break the page after the publish beacon was sent.
  try {
    const cached = env.storage?.getItem(cacheKey(serveUrl));
    if (cached) {
      const payload = parseServePayload(JSON.parse(cached));
      if (payload && isTerminalStatus(payload.status)) {
        applyIfChanged(payload);
        // The cached payload is terminal — publish `stopped` with reason `suppressed`
        // so the diagnostics surface reflects "the loop never started" rather than
        // leaving it in the default `polling` state from the initial snapshot.
        diag?.stop("suppressed");
        return;
      }
    }
  } catch {
    // Throwing read, corrupt JSON, or a payload that won't parse — ignore the cache entry
    // and poll fresh.
  }

  // The PostHog correlation sink (BRYN-1601), fed from the poll rather than from
  // `applyServePayload` for two reasons: it must run on every response, including the ones whose
  // payload is unchanged (that is how a late-loading posthog-js and a mid-session `identify()`
  // get picked up), and running it after `applyIfChanged` means a tenant's own
  // `bryn:personalize` listener — its `identify()`, typically — has already run, so our write
  // lands last, on the person they just established.
  const syncPosthog = createPosthogSync(env);

  // Bounded until the first resolution, then unbounded (BRYN-1008). A visitor that only
  // ever gets `pending` (a cold visitor who never resolves, or a return-leg-disabled tenant
  // that serves `pending` by design) stops after RETRIEVE_MAX_PENDING_ATTEMPTS rather than
  // polling for the tab's lifetime and burning that tenant's serve/rate-limit budget. Once
  // `resolvedSeen` flips, the cap is lifted so the pixel keeps polling at the steady cadence
  // for fresher entity/individual data.
  let resolvedSeen = false;
  // Consecutive serve failures (rejection OR `ok: false`); reset on any parsed payload so a
  // 503-then-200 is fine. At RETRIEVE_MAX_CONSECUTIVE_FAILURES the loop ends.
  let consecutiveFailures = 0;
  // `lastAppliedAt` is set by `applyIfChanged` and consulted at the moment we are about to
  // schedule the next poll; tracking the variable here, rather than reading it out of an
  // argument, lets the visibility re-arm path share the same idle-ceiling logic.
  let loopEnded = false;
  // A poll waiting on visibility. The attempt number is captured so we resume the same
  // backoff counter — a parked poll is not an attempt, so the pending-window cap does not
  // tick while the tab is hidden, and `consecutiveFailures` does not tick either.
  let parkedAttempt: number | null = null;
  // Why the loop ended. Only an idle stop is re-armed by a visibility change: every other
  // reason (suppressed, collection-only, the failure and pending ceilings, unload) is final.
  let stopReason: ServeStopReason | undefined;

  // The exits the sink must be told about (each calls `syncPosthog.stop()` AND
  // publishes the stop reason to diagnostics):
  //  - pending-attempt ceiling, reachable only while never-resolved;
  //  - consecutive-failure ceiling, in either phase;
  //  - resolved-phase idle ceiling, while the served data hasn't changed in 30 minutes;
  //  - host unload (pagehide/beforeunload fired);
  //  - collection-only, when a response carries `X-Bryn-Return-Leg: off` (BRYN-1960).
  // `suppressed` is handled inline (it is the same `return` that ends the loop, after `stop()`).
  const endLoop = (reason: ServeStopReason): void => {
    loopEnded = true;
    stopReason = reason;
    parkedAttempt = null;
    syncPosthog.stop();
    diag?.stop(reason);
  };

  // Schedule the next attempt, or park it if the tab is hidden. Returns true when the
  // attempt was actually scheduled (or fired immediately) and false when it was parked — used
  // by the idle-ceiling check so we can tell "loop is still healthy" from "loop has ended".
  const scheduleOrPark = (n: number, delay: number): void => {
    if (loopEnded) return;
    if (env.visibility?.hidden()) {
      parkedAttempt = n;
      // The poll is parked, so `loopState` flips to `paused` until the next visibility
      // event fires it. Publishing here rather than in the visibility handler keeps the
      // transition local to the place that made it: every place that mutates `loopState`
      // does its own publish, with no double-tracking in `onVisibilityChange`.
      diag?.serve({ loopState: "paused" });
      return;
    }
    diag?.serve({ loopState: "polling" });
    schedule(() => attempt(n), delay);
  };

  const scheduleNext = (n: number): void => {
    if (consecutiveFailures >= RETRIEVE_MAX_CONSECUTIVE_FAILURES) {
      endLoop("consecutive-failures");
      return;
    }
    if (!resolvedSeen && n + 1 >= RETRIEVE_MAX_PENDING_ATTEMPTS) {
      // Giving up on a visitor who never resolved — and the correlation sink may well be ARMED,
      // since a `pending` payload that names an account is written (BRYN-1601). With no further
      // polls the authorisation verdict can never be re-checked, so a tenant who switched the
      // correlation off would never be honoured on this tab while the identify hook kept
      // writing. Stand the sink down with the loop.
      endLoop("pending-ceiling");
      return;
    }
    // Resolved-phase idle ceiling: a quiet tab whose served payload hasn't changed in 30
    // minutes is not going to change in the next minute either. End the loop and stand the
    // sink down; a visibilitychange while idle re-arms one poll, then the window restarts.
    // `env.now` is optional for hosts that wire only the cache leg; without it the idle
    // ceiling is disabled and the loop runs the previous (BRYN-1008) "keep polling for the
    // tab's lifetime" behaviour.
    if (resolvedSeen && lastAppliedAt !== undefined && env.now && env.now() - lastAppliedAt >= RETRIEVE_RESOLVED_IDLE_MS) {
      endLoop("idle-ceiling");
      return;
    }
    scheduleOrPark(n + 1, backoffDelay(n, RETRIEVE_BASE_MS, RETRIEVE_STEADY_MS));
  };

  // Visibility handler. Three things can happen on a `visibilitychange`:
  //  1. A poll was parked (tab went hidden with a due poll in flight) and the tab is back —
  //     run the parked poll immediately, no delay, no extra backoff tick. The pending-window
  //     counter and the consecutive-failure counter do not move (parking is not an attempt).
  //  2. The loop was ended by the idle ceiling AND the tab is now visible — re-arm: one
  //     poll at attempt 0, then the idle window restarts. The single re-arm poll is a
  //     fresh attempt: it CAN trip the failure ceiling or apply a fresh payload.
  //  3. The loop is healthy and the tab is hidden — nothing to do; the parked poll is
  //     already enqueued for the next visible event.
  const onVisibilityChange = (): void => {
    if (!env.visibility || env.visibility.hidden()) return;
    if (parkedAttempt !== null && !loopEnded) {
      const n = parkedAttempt;
      parkedAttempt = null;
      diag?.serve({ loopState: "polling" });
      attempt(n);
      return;
    }
    if (loopEnded && stopReason === "idle-ceiling") {
      // Re-arm from idle: one poll, then the ceiling restarts. `loopEnded` is the trigger
      // and gets cleared here so a second visibility event does not fire another re-arm.
      loopEnded = false;
      stopReason = undefined;
      consecutiveFailures = 0;
      lastAppliedAt = env.now?.();
      // Clear `stopReason` on the diagnostics surface too so the snapshot stops reporting
      // `polling` alongside the now-stale `idle-ceiling` reason (the documented contract is
      // `stopReason` is set only when `loopState === "stopped"`).
      diag?.serve({ loopState: "polling", stopReason: undefined });
      attempt(0);
    }
  };
  env.visibility?.onChange(onVisibilityChange);

  // Named arrow for the first .then of `attempt`: parses the serve response into the
  // three-tuple (raw body, posthog-sync verdict, numeric status). Split out so the
  // attempt() chain itself stays small and the three branches (ok+parsed, ok+unparseable,
  // !ok) are each readable on their own.
  const parseServeResponse = (
    res: ServeResponseLike,
  ): Promise<{ raw: unknown; syncAllowed: boolean; status: number; legOff: boolean }> => {
    // `status` is defaulted to `0` when the host doesn't surface one — the structural
    // type models it as optional precisely because fetch wrappers vary. `lastOutcome` collapses
    // `0` to `network-error` for the same reason.
    const status = res.status ?? 0;
    if (res.ok) {
      // The return-leg-off header is re-read on every response (BRYN-1960), with the same
      // CORS / missing-accessor guard as `posthogSyncAllowed`; the loop ends on the first `off`.
      return res
        .json()
        .then((raw) => ({ raw, syncAllowed: posthogSyncAllowed(res), status, legOff: returnLegOff(res) }));
    }
    return Promise.resolve({ raw: null, syncAllowed: false, status, legOff: false });
  };

  // A parsed payload is a success: reset the consecutive-failure counter (a 503 followed by a
  // 200 is fine) and surface the payload.
  const applyParsedPayload = (payload: ServePayload, syncAllowed: boolean): void => {
    consecutiveFailures = 0;
    // Once resolved, ignore a transient `pending` (a serve-error fallback or an
    // expired/absent identity lookup): applying it would downgrade window.bryn and
    // the `bryn:personalize` event to cold and re-hide `status=resolved` elements,
    // while the already-written `data-bryn-field` text stays put, leaving the page
    // in a mixed state. Keep the personalized surfaces and keep polling for fresher
    // resolved data instead. A real "stop showing" is the terminal `suppressed`.
    // BEFORE the dispatch below, not after. `applyIfChanged` dispatches
    // `bryn:personalize`; a tenant listener on it commonly calls `posthog.identify()`, and
    // dispatch -> their listener -> `$identify` -> our hook -> `flush` is all synchronous.
    // `receive` is where every stop decision lands, so a withdrawn authorisation or a
    // terminal `suppressed` is already in effect for that re-entry rather than one step late.
    syncPosthog.receive(payload, syncAllowed);
    if (!(resolvedSeen && payload.status === "pending")) applyIfChanged(payload);
    if (payload.status === "resolved") resolvedSeen = true;
    // AFTER, so a tenant `identify()` listener has already run and our write lands last,
    // on the person they just established.
    syncPosthog.write(payload);
    diag?.serve({ lastOutcome: "ok", consecutiveFailures: 0 });
  };

  // `!res.ok`, or a 200 whose body is not our payload: there is nothing to apply, and only the
  // next poll can recover, so both count toward the consecutive-failure cap.
  const recordServeFailure = (status: number): void => {
    consecutiveFailures += 1;
    diag?.serve({ lastOutcome: serveFailureOutcome(status), consecutiveFailures });
  };

  // End the loop when this response is its last: a terminal `suppressed` (cached, so later
  // pages skip the poll) or a collection-only `X-Bryn-Return-Leg: off` (BRYN-1960, not cached).
  // Returns whether the loop ended.
  const endOnFinalResponse = (payload: ServePayload | null, legOff: boolean): boolean => {
    if (payload && isTerminalStatus(payload.status)) {
      try {
        env.storage?.setItem(cacheKey(serveUrl), JSON.stringify(payload));
      } catch {
        // Storage unavailable/full — non-fatal; the payload is already applied.
      }
      endLoop("suppressed");
      return true;
    }
    if (legOff) {
      endLoop("collection-only");
      return true;
    }
    return false;
  };

  // The second .then of `attempt`. An in-flight response that resolves after the loop ended
  // (unload, or any other end) is dropped: applying it would re-authorise listeners the loop
  // has already disarmed.
  const handleParsedResponse =
    (n: number) =>
    (parsed: { raw: unknown; status: number; syncAllowed: boolean; legOff: boolean }): void => {
      if (loopEnded) return;
      const payload = parseServePayload(parsed.raw);
      if (payload) applyParsedPayload(payload, parsed.syncAllowed);
      else recordServeFailure(parsed.status);
      if (!endOnFinalResponse(payload, parsed.legOff)) scheduleNext(n);
    };

  // Named arrow for the .catch of `attempt`: a fetch rejection (network or extension
  // block) bumps the failure counter and surfaces a `network-error` on the diagnostics
  // before scheduling the next poll. Kept as its own arrow so the per-rejection
  // behaviour reads at a glance — it used to be a four-line `.catch()` body buried
  // inside `attempt`.
  const onAttemptFailure = (n: number) => (): void => {
    // Drop the rejection on the floor if the loop has already ended (host-unload, in
    // particular) — bump-the-counter would also bump the failure cap and re-schedule a
    // poll that has no authority to run.
    if (loopEnded) return;
    consecutiveFailures += 1;
    diag?.serve({ lastOutcome: "network-error", consecutiveFailures });
    scheduleNext(n);
  };

  const attempt = (n: number): void => {
    // A timer queued before the loop ended (unload, `suppressed`, a ceiling) still fires. It
    // must neither fetch nor park itself and republish `paused` over the stopped diagnostics,
    // so this runs before the visibility check. In-flight responses are dropped by the same
    // guard in `handleParsedResponse` / `onAttemptFailure`.
    if (loopEnded) return;
    // Re-check visibility at fire time, not just at scheduling time: the timer fires regardless,
    // and a tab hidden between `scheduleOrPark` and the timer would otherwise spend an attempt
    // (and could trip the failure cap on a parked network). Park and let the visibility handler
    // resume it.
    if (env.visibility?.hidden()) {
      parkedAttempt = n;
      diag?.serve({ loopState: "paused" });
      return;
    }
    fetchServe(serveUrl).then(parseServeResponse).then(handleParsedResponse(n)).catch(onAttemptFailure(n));
  };

  // Host-unload handler (BRYN-1962). End the loop on a real unload — the visitor navigates
  // away or closes the tab — so the PostHog sink
  // and the diagnostics publisher stand down rather than holding listeners that outlive
  // the loop's authority to re-authorise them. Idempotent: `endLoop` is safe to call on
  // an already-ended loop (it just sets `loopEnded = true` again).
  env.pageUnload?.onHide((persisted) => {
    // A page entering the back/forward cache is frozen, not destroyed: its timers pause and
    // resume on restore, and the restore fires `visibilitychange`, which runs any parked poll.
    // Ending the loop here would leave a restored page permanently stopped, because the
    // script does not rerun.
    if (persisted) return;
    endLoop("host-unload");
  });

  // The initial schedule is also routed through `scheduleOrPark`: a page that is already
  // hidden when the pixel loads (background tab on first paint) parks the very first poll
  // rather than firing it on the start delay. The parked poll runs immediately on the next
  // visibilitychange, so the visitor still gets served as soon as they return.
  scheduleOrPark(0, RETRIEVE_START_MS);
};

/**
 * Resolves config from the host script, sends one publish beacon, then arms the
 * return leg (BRYN-594): polls the serve endpoint and surfaces the payload inline.
 *
 * Publish-leg outcome is published to the diagnostics publisher (BRYN-1962) as
 * soon as `send` returns, so the publish state on `window.__brynPixelDiagnostics`
 * is the first thing a support console sees. The serve loop publishes its own
 * transitions from inside `startRetrieve`.
 *
 * The single-instance guard (BRYN-1958) and the `querySelectorAll`-based script locator
 * (BRYN-1972) are wired in here, not inside `findScript`/`claim`: the guard reads a
 * cross-realm global registry that `findScript` is forbidden from touching, and the
 * claim has to happen synchronously — before `send` and `startRetrieve`, before any
 * await — so two installs running back to back cannot both see the same tag as
 * unclaimed. `init` runs straight through with no awaits between `findScript` and
 * `claim`.
 *
 * `personalize` (BRYN-1960) gates the return leg: when it is `false`, the publish beacon
 * still fires (a customer's events still flow into the pipeline), but `startRetrieve` is
 * skipped — no poll, no `window.bryn`, no `bryn:personalize`, no PostHog correlation.
 * Collection-only, not collection-also-stopped.
 */
export const init = (env: PixelEnv, fallback?: PixelGlobalConfig | null): boolean => {
  const config = resolveConfig(findScript(env), fallback);
  if (!config) return false;
  // BRYN-1958: a second install of the same bundle (the site + GTM double-install path) calls
  // `autostart` again with the same pixel ref; the registry claims the first one and this
  // returns false. We deliberately do not run `send` or `startRetrieve` after a failed claim
  // so the dedup is end-to-end, not just on the publish leg — the second script would otherwise
  // post an extra `pixel.page-viewed` AND arm its own poll loop with its own PostHog sink.
  if (env.claim && !env.claim(config.pixelRef)) return false;
  // Construct the diagnostics publisher lazily — after the claim — so a second install of
  // the same bundle never writes a fresh, all-defaults snapshot onto `window.__brynPixelDiagnostics`
  // before its `init` bails out at the claim. Tests that already pass a stub publisher via
  // `env.diagnostics` are unaffected.
  if (env.diagnostics === undefined && env.diagnosticsInputs) {
    env.diagnostics = createDiagnosticsPublisher(env.diagnosticsInputs.global, env.diagnosticsInputs.debug).publisher;
  }
  const sent = send(
    env,
    config.endpoint,
    buildPayload(env.location, env.document, env.now(), env.storage),
  );
  env.diagnostics?.publish({ state: sent });
  if (config.personalize) startRetrieve(env, retrieveUrl(config));
  // The snippet opt-out never starts the loop, so record why rather than leave the
  // diagnostics reading `polling` for a loop that does not exist.
  else env.diagnostics?.stop("collection-only");
  return sent !== "unavailable" && sent !== "failed";
};

/**
 * Read `window.sessionStorage` without letting the page break. The property ACCESS itself throws
 * (SecurityError) in a browser configured to block site data, so this cannot be an inline
 * `global.sessionStorage` — that would take down the publish leg with it, which is exactly what
 * this module must never do. `undefined` disables the first-touch replay and the serve cache and
 * changes nothing else.
 */
const safeSessionStorage = (global: PixelGlobal): StorageLike | undefined => {
  try {
    return global.sessionStorage;
  } catch {
    return undefined;
  }
};

// ---- Init-time single-instance guard (BRYN-1958) ----
//
// The site + GTM Custom HTML path installs the pixel twice on one page: once in the host HTML
// and once via `buildPixelGtmSnippet` (`apps/control-plane-api/src/onboarding/onboard.ts`). Each
// install runs `autostart` with its own module scope, so module-level state is not shared
// between the two scripts. Without a guard, both run `autostart` -> `init` -> `sendBeacon`,
// both arm their own poll loop, and both install their own PostHog sink — the page becomes
// two beacons per view, two poll loops, and (since `pixel.*` Events have NO default dedup path)
// two Events per page view. The fix is a per-page claim keyed by pixel ref: the second
// install sees the ref already claimed and bails before sending.
//
// `Symbol.for` (the cross-realm symbol registry), NOT a module constant, because each script
// tag loads the bundle as its own IIFE; the registry has to live on `globalThis`, not on the
// script's own module state. `Symbol.for` is the platform-blessed shared symbol: any realm
// on the page that asks for `Symbol.for("bryn.pixel.init")` gets the same symbol, and the
// value lives on the same `globalThis` slot.
//
// The value itself is a `Set<string>` of claimed pixel refs. Two distinct refs on one page
// (two tenants sharing an embedder) must both run, so the keying is per-ref, not a single
// boolean. An untrusted value at the slot (some other script, a previous Bryn build, an
// attacker) is ignored: if it isn't a `Set`, we replace it with a fresh one IF we can write
// the slot, otherwise we treat the registry as unavailable.
//
// The whole thing is wrapped in `try` and fails OPEN (returns `true`, the claim succeeds): a
// pixel that silently never starts is worse than the duplicate we're trying to prevent, since
// the duplicate is at least one working beacon and a missing one is none. Failing closed
// would convert "two instances" into "sometimes zero" on a hostile page.

/** The shared symbol key under which pixel installs record their claim on the page. */
const INIT_CLAIM_SYMBOL = Symbol.for("bryn.pixel.init");

/**
 * Claim a pixel ref for this install of the bundle. The first caller for a given ref returns
 * true and the bundle proceeds; the second caller for the same ref returns false and the
 * bundle bails before `send`/`startRetrieve`. A registry that throws, holds a non-Set value,
 * or can't be written returns true (fail open) — see the rationale on INIT_CLAIM_SYMBOL.
 */
const claimInitSlot = (global: PixelGlobal, pixelRef: string): boolean => {
  try {
    const slot = global as unknown as Record<symbol, unknown>;
    let registry: Set<string> | undefined =
      slot[INIT_CLAIM_SYMBOL] instanceof Set ? (slot[INIT_CLAIM_SYMBOL] as Set<string>) : undefined;
    if (!registry) {
      // Slot is empty or holds an untrusted value: try to replace it with a fresh Set. A page
      // that defines the symbol key as a non-writable property (or a getter with no setter)
      // will throw on the assignment — fall back to failing open by running as a one-off.
      registry = new Set<string>();
      slot[INIT_CLAIM_SYMBOL] = registry;
    }
    if (registry.has(pixelRef)) return false;
    registry.add(pixelRef);
    return true;
  } catch {
    // Reading the slot, the `instanceof` check, or the `add`/`has` calls threw — host page
    // went hostile on the registry. Run anyway; a duplicate is recoverable, a missing pixel
    // means a customer is running blind.
    return true;
  }
};

/**
 * Initial diagnostics snapshot. `publish.state` is `unavailable` because no beacon
 * has been sent yet; `serve.lastOutcome` is `network-error` because nothing has
 * succeeded; `consecutiveFailures` starts at 0; `loopState` is `polling` because
 * the loop is armed. These defaults match what an idle page that never sees a
 * visibility event should report to a customer reading the global.
 */
const INITIAL_DIAGNOSTICS: PixelDiagnostics = {
  publish: { state: "unavailable" },
  serve: { lastOutcome: "network-error", consecutiveFailures: 0, loopState: "polling" },
};

/**
 * Equality check on `PixelDiagnostics` snapshots, used to suppress no-op transitions
 * (a state mutation that does not change the diagnostic surface — e.g. writing the
 * same `consecutiveFailures` number twice — would still fire a `bryn:delivery`
 * event without this guard, and support consoles that subscribe would log a flurry
 * of identical transitions).
 */
const diagnosticsEqual = (a: PixelDiagnostics, b: PixelDiagnostics): boolean => {
  if (a.publish.state !== b.publish.state) return false;
  if (a.serve.lastOutcome !== b.serve.lastOutcome) return false;
  if (a.serve.consecutiveFailures !== b.serve.consecutiveFailures) return false;
  if (a.serve.loopState !== b.serve.loopState) return false;
  return a.serve.stopReason === b.serve.stopReason;
};

/**
 * Wrap `console.debug` so the debug gate is enforced in exactly one place. The
 * pixel itself NEVER calls `console.*` without going through this helper, so
 * `data-bryn-debug` / `debug: true` is the only way to get any console output
 * from the script. A `debug` arg of `false` is a true no-op — no allocation,
 * no message-format cost.
 */
const debugLog = (enabled: boolean, message: string, meta?: Record<string, unknown>): void => {
  if (!enabled) return;
  if (meta === undefined) {
    console.debug(message);
  } else {
    console.debug(message, meta);
  }
};

/**
 * Read-only counterpart to {@link claimInitSlot} (BRYN-1972). `findScript` consults this on
 * the `querySelectorAll` fallback to pick the first tag whose ref has not yet been claimed.
 * Returns false on any failure — there is no registry to consult, so the question is moot,
 * and the first matching tag wins (the pre-BRYN-1972 behaviour for a host that wires
 * `isClaimed` but no registry).
 */
const isInitSlotClaimed = (global: PixelGlobal, pixelRef: string): boolean => {
  try {
    const slot = global as unknown as Record<symbol, unknown>;
    const registry = slot[INIT_CLAIM_SYMBOL];
    return registry instanceof Set && registry.has(pixelRef);
  } catch {
    return false;
  }
};

/**
 * Build the diagnostics publisher for one page. The snapshot is held on this
 * closure and ALSO assigned to `window.__brynPixelDiagnostics`; the page can read
 * either for the latest state, and subscribers to `bryn:delivery` receive the
 * same reference on every event so a captured handle stays current without
 * re-reading the global.
 *
 * `update` is split into `publish` / `serve` rather than a single `update` so the
 * call sites in `init` and `startRetrieve` read as "publish moved, serve moved"
 * rather than "update with a partial". The runtime behaviour is identical.
 *
 * The publisher never throws. Both writes (assigning to the global) and the
 * CustomEvent dispatch are wrapped in their own try/catch so a hostile page
 * that defines the global as a non-writable accessor, or replaces
 * `CustomEvent` with a throwing constructor, cannot break the beacon's poll
 * loop. This mirrors the wrapping around `global.bryn` in `autostart`.
 */
export const createDiagnosticsPublisher = (
  global: PixelGlobal,
  debug: boolean,
): { publisher: DiagnosticsPublisher; snapshot: PixelDiagnostics } => {
  const snapshot: PixelDiagnostics = {
    publish: { ...INITIAL_DIAGNOSTICS.publish },
    serve: { ...INITIAL_DIAGNOSTICS.serve },
  };
  const CustomEventImpl = global.CustomEvent;
  const dispatchEvent = global.dispatchEvent;

  const writeGlobal = (): void => {
    try {
      global.__brynPixelDiagnostics = snapshot;
    } catch {
      // Host page defines the property as non-writable or a throwing setter — fall
      // back to the in-closure snapshot, which is what subscribers to `bryn:delivery`
      // read off `event.detail`. The poll loop is unaffected.
    }
  };

  const dispatchChange = (prev: PixelDiagnostics): void => {
    if (dispatchEvent && CustomEventImpl) {
      try {
        dispatchEvent.call(global, new CustomEventImpl(DELIVERY_EVENT, { detail: snapshot }));
      } catch {
        // Best-effort — a throwing CustomEvent constructor or dispatchEvent must not
        // break the poll loop.
      }
    }
    debugLog(debug, "bryn:delivery", { diagnostics: snapshot, from: prev });
  };

  // Snapshot the previous state, run the caller's mutation, and only fan out (write the
  // global + dispatch the CustomEvent) when the snapshot actually changed. Centralising
  // the no-op-suppression check avoids the three near-identical publisher methods each
  // repeating the same clone / mutate / equality / fan-out dance.
  const applyAndDispatch = (mutate: () => void): void => {
    const prev: PixelDiagnostics = {
      publish: { ...snapshot.publish },
      serve: { ...snapshot.serve },
    };
    mutate();
    if (diagnosticsEqual(snapshot, prev)) return;
    writeGlobal();
    dispatchChange(prev);
  };

  const publisher: DiagnosticsPublisher = {
    publish: (partial) => applyAndDispatch(() => Object.assign(snapshot.publish, partial)),
    serve: (partial) => applyAndDispatch(() => Object.assign(snapshot.serve, partial)),
    stop: (reason) =>
      applyAndDispatch(() => {
        snapshot.serve.loopState = "stopped";
        snapshot.serve.stopReason = reason;
      }),
  };

  // Publish the initial snapshot so a page reading `window.__brynPixelDiagnostics`
  // before any state change still sees the documented object shape. No CustomEvent
  // for the initial write: nothing changed.
  writeGlobal();
  return { publisher, snapshot };
};

/**
 * The page-unload surface for {@link startRetrieve} (BRYN-1962): the handler receives
 * `pagehide`'s `persisted` flag, true when the page is entering the back/forward cache rather
 * than being destroyed. `beforeunload` is only a fallback for an `addEventListener` that throws
 * on `pagehide`; it is not feature detection (an unsupported event name is accepted silently
 * and never fires), and every browser the pixel targets fires `pagehide`. Undefined when the
 * host has no `addEventListener`, in which case the loop's listeners die with the page.
 */
const pageUnloadSurface = (global: PixelGlobal): PixelEnv["pageUnload"] => {
  const listen = global.addEventListener;
  if (!listen) return undefined;
  const tryListen = (type: string, handler: (event: unknown) => void): boolean => {
    try {
      listen.call(global, type, handler);
      return true;
    } catch {
      return false;
    }
  };
  const onHide = (handler: (persisted: boolean) => void): void => {
    if (tryListen("pagehide", (event) => handler(isRecord(event) && event.persisted === true))) return;
    // If this fails too, the loop simply outlives the page.
    tryListen("beforeunload", () => handler(false));
  };
  return { onHide };
};

/**
 * Browser entry point: wires the ambient globals into {@link init}. A no-op when
 * there is no document or location (e.g. server-side rendering or a test import);
 * with a document but no resolvable pixel `<script>` (see {@link findScript}), init
 * itself no-ops.
 */
export const autostart = (global: PixelGlobal): boolean => {
  if (!global.document || !global.location) return false;
  const BlobImpl = global.Blob;
  const doc = global.document;

  // Return-leg wiring (all optional — a missing global just disables that capability).
  const rawFetch = global.fetch as unknown as
    | ((url: string, init?: Record<string, unknown>) => Promise<ServeResponseLike>)
    | undefined;
  const serveFetch: ServeFetchLike | undefined = rawFetch
    ? (url) => rawFetch(url, { method: "GET", credentials: "include", mode: "cors" })
    : undefined;
  const CustomEventImpl = global.CustomEvent;
  const dispatchEvent = global.dispatchEvent;
  const dispatch: DispatchLike | undefined =
    CustomEventImpl && dispatchEvent
      ? (type, detail) => {
          dispatchEvent.call(global, new CustomEventImpl(type, { detail }));
        }
      : undefined;
  const queryAll: QueryAllLike | undefined = doc.querySelectorAll
    ? (selector) => doc.querySelectorAll!(selector) as ArrayLike<ElementLike>
    : undefined;
  // Publish the latest payload to `window.bryn` for synchronous reads (decision 4a) —
  // a customer can read `window.bryn` without registering a `bryn:personalize` listener.
  const publish: PublishLike = (payload) => {
    global.bryn = payload;
  };

  // Diagnostics publisher (BRYN-1962). Read the fallback `__brynPixel` config to decide
  // whether to enable `console.debug` lines for transitions — `data-bryn-debug` on the
  // host script is the tag-manager path (no script attribute support), `debug: true`
  // on the global is the page-script path. The publisher itself is always on: the
  // global + CustomEvent are the customer-support surface, gated only on console
  // output. Construction is deferred to `init` (after the claim) so a second install
  // of the same bundle never overwrites the live snapshot before its `init` bails.
  const fallback = readGlobalConfig(global.__brynPixel);
  // Same lookup `init` makes, so the flag is read off the tag this install will claim.
  const debugFromScript =
    findScript({
      document: doc,
      location: global.location,
      now: () => Date.now(),
      isClaimed: (pixelRef) => isInitSlotClaimed(global, pixelRef),
    })?.getAttribute("data-bryn-debug") === "true";
  const debug = (fallback?.debug ?? false) || debugFromScript;

  return init(
    {
      document: global.document,
      location: global.location,
      navigator: global.navigator,
      fetch: global.fetch,
      blob: BlobImpl ? (body, type) => new BlobImpl([body], { type }) : undefined,
      now: () => Date.now(),
      serveFetch,
      setTimeout: global.setTimeout ? (handler, ms) => global.setTimeout!(handler, ms) : undefined,
      storage: safeSessionStorage(global),
      dispatch,
      publish,
      queryAll,
      // Read lazily on every poll — PostHog's loader commonly runs after us (BRYN-1601).
      posthog: () => global.posthog,
      // Page Visibility API (BRYN-1959). `document.visibilityState` is read on demand
      // rather than captured — the same reasoning as `posthog()` above: a value captured at
      // init would be pinned for the whole page even though the tab visibility flips many
      // times over a long-lived session. The listener is wired once on autostart and the
      // callback drives both the parked-poll re-arm and the idle-ceiling re-arm in
      // `startRetrieve`. Both `visibilityState` and `addEventListener` are optional in the
      // type so a host that wires neither runs the unbounded loop as before — `startRetrieve`
      // reads `env.visibility` and silently takes the no-visibility branch when it is absent.
      visibility:
        doc.visibilityState && doc.addEventListener
          ? {
              hidden: () => doc.visibilityState === "hidden",
              onChange: (handler) => doc.addEventListener!("visibilitychange", handler),
            }
          : undefined,
      pageUnload: pageUnloadSurface(global),
      diagnosticsInputs: { global, debug },
      // BRYN-1958: see INIT_CLAIM_SYMBOL for the cross-realm symbol-key registry rationale.
      claim: (pixelRef) => claimInitSlot(global, pixelRef),
      // BRYN-1972: read-only counterpart consulted by `findScript`'s `querySelectorAll`
      // fallback. Reads the same registry `claim` writes — both share INIT_CLAIM_SYMBOL.
      isClaimed: (pixelRef) => isInitSlotClaimed(global, pixelRef),
    },
    fallback,
  );
};

autostart(globalThis as unknown as PixelGlobal);
